import {
  StrictMode,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactElement
} from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  AppWindow,
  BarChart3,
  Bell,
  BookOpen,
  Braces,
  Boxes,
  CheckCircle2,
  Code2,
  Compass,
  Copy,
  Database,
  Eye,
  FileCode2,
  FileText,
  GitBranch,
  HelpCircle,
  Home,
  Image as ImageIcon,
  Layers3,
  LogOut,
  MousePointer2,
  Palette,
  Play,
  RefreshCw,
  Save,
  ShieldCheck,
  SlidersHorizontal,
  Settings,
  Trash2,
  User
} from "lucide-react";
import "@alpha/tokens/styles.css";
import "@alpha/ui/styles.css";
import "@alpha/patterns/styles.css";
import {
  Alerte,
  Badge,
  BarreLateraleModerne,
  Bouton,
  BoutonIcone,
  Carte,
  ChampTexte,
  EtiquetteChamp,
  GroupeActions,
  GroupeChamp,
  IndicateurChargement,
  Interrupteur,
  ListeDeroulante,
  MessageErreur,
  Panneau,
  ZoneTexte
} from "@alpha/ui";
import { TableauDonnees } from "@alpha/patterns";
import {
  enregistrerJetonStudio,
  ErreurApi,
  lireJetonStudio,
  requeteApi,
  supprimerJetonStudio,
  type BrouillonStudio,
  type ComposantCatalogue,
  type EditeurToken,
  type ProfilApparence,
  type PromotionBrouillon,
  type ReponseConnexion,
  type ReponseTokens,
  type SessionStudio,
  type SurchargeToken
} from "./api";
import "./styles.css";

const VueEditeurGraphique = lazy(async () => {
  const module = await import("./editeur-graphique");
  return { default: module.VueEditeurGraphique };
});

const VueEditeurComposant = lazy(async () => {
  const module = await import("./editeur-composant");
  return { default: module.VueEditeurComposant };
});

const VueCharteGraphique = lazy(async () => {
  const module = await import("./charte-studio");
  return { default: module.VueCharteGraphique };
});

const VueApplicationsAlpha = lazy(async () => {
  const module = await import("./applications-alpha");
  return { default: module.VueApplicationsAlpha };
});

type VueStudio =
  | "guide"
  | "controle"
  | "charte"
  | "applications"
  | "atelier"
  | "tokens"
  | "bibliotheque"
  | "catalogue"
  | "composant"
  | "import"
  | "graphique"
  | "live"
  | "validation";
type EtatSession = "verification" | "connexion" | "pret";

interface DefinitionVue {
  id: VueStudio;
  libelle: string;
  icone: ReactElement;
  accrocheNavigation: string;
  resume: string;
  objectif: string;
  utilisation: string[];
  impact: string;
  actionPrincipale: string;
  exemples: ExempleGuide[];
}

interface ExempleGuide {
  titre: string;
  situation: string;
  action: string;
  resultat: string;
}

interface ExempleSimple {
  titre: string;
  description: string;
  details: string[];
}

interface ExempleProfil {
  titre: string;
  description: string;
  profil: ProfilApparence;
  details: string[];
}

interface ExempleToken {
  titre: string;
  chemin: string;
  theme: string;
  valeur: string;
  description: string;
  details: string[];
}

type ModeImportStudio = "code" | "texte" | "url";

interface ExempleImportStudio {
  titre: string;
  mode: ModeImportStudio;
  nom: string;
  url: string;
  contenu: string;
  description: string;
  details: string[];
}

interface ConceptStudio {
  nom: string;
  definition: string;
  utilite: string;
  impact: string;
  fonctionnement: string;
  exemples: string[];
  action: VueStudio;
}

interface ParcoursStudio {
  titre: string;
  objectif: string;
  depart: VueStudio;
  etapes: string[];
  resultat: string;
}

interface ElementBibliotheque {
  nom: string;
  famille: string;
  statut: "Disponible" | "Pattern" | "Exemple à créer";
  sertA: string;
  quandUtiliser: string[];
  agitSur: string;
  exemples: string[];
  import?: string;
}

interface EtatDonnees {
  chargement: boolean;
  catalogue: ComposantCatalogue[];
  brouillons: BrouillonStudio[];
  profils: ProfilApparence[];
  tokens: ReponseTokens | null;
  git: StatutGit | null;
  erreur: string;
}

interface StatutGit {
  branche: string;
  commit: string;
  statut: string;
  diffstat: string;
}

interface RapportImport {
  statut: string;
  source: string;
  composantsDetectes: string[];
  erreurs: string[];
  avertissements: string[];
  taille: number;
  apercu: string;
}

const donneesInitiales: EtatDonnees = {
  chargement: false,
  catalogue: [],
  brouillons: [],
  profils: [],
  tokens: null,
  git: null,
  erreur: ""
};

const profilApparenceInitial: ProfilApparence = {
  id: "",
  nom: "Exploration Alpha",
  description: "",
  famillePolice: "Inter, ui-sans-serif, system-ui, sans-serif",
  tailleBase: 16,
  tailleTitre: 28,
  graisseTitre: 700,
  styleCarte: "equilibre",
  couleurPrincipale: "#0067b8",
  couleurSurface: "#ffffff",
  couleurTexte: "#15202b",
  couleurAccent: "#eef6ff",
  gradientDepart: "#0067b8",
  gradientArrivee: "#0f7b4b",
  angleDegres: 135,
  imageUrl: "",
  arrondi: 8,
  intensiteOmbre: 12,
  modifieLe: ""
};

const vues: DefinitionVue[] = [
  {
    id: "guide",
    libelle: "Comprendre",
    icone: <BookOpen size={18} />,
    accrocheNavigation: "Définitions et parcours",
    resume: "Point d'entrée pédagogique pour comprendre les notions avant de modifier.",
    objectif:
      "Expliquer les concepts du design system avec des exemples concrets et orienter vers le bon écran.",
    utilisation: [
      "Choisissez un objectif de travail pour suivre un parcours.",
      "Choisissez ce que vous voulez modifier : couleurs, boutons, formulaires ou exemples.",
      "Ouvrez ensuite l'écran correspondant. Le Studio applique les réglages techniques en arrière-plan."
    ],
    impact: "Lecture seule. Cet écran explique et oriente, il ne modifie aucun fichier.",
    actionPrincipale: "Les boutons de parcours ouvrent l'écran utile pour l'objectif choisi.",
    exemples: [
      {
        titre: "Je veux changer une couleur",
        situation: "Vous voulez changer le bleu principal sans comprendre les fichiers internes.",
        action: "Ouvrez Charte graphique, puis modifiez Couleur principale dans les réglages visuels.",
        resultat:
          "Les boutons, badges et éléments actifs changent immédiatement dans l'aperçu."
      },
      {
        titre: "Je veux ajouter un composant",
        situation: "Vous avez une idée de composant ou un bout de code existant.",
        action: "Cherchez d'abord s'il existe déjà, puis ouvrez Importer seulement si vous devez garder une nouvelle idée.",
        resultat: "Vous savez ce qui est seulement brouillon et ce qui existe déjà réellement."
      },
      {
        titre: "Je veux vérifier avant livraison",
        situation: "Vous avez modifié l'interface ou la charte graphique.",
        action: "Lisez Git et Validation puis ouvrez Valider.",
        resultat: "Vous lancez la commande adaptée au changement."
      }
    ]
  },
  {
    id: "controle",
    libelle: "Contrôler",
    icone: <Database size={18} />,
    accrocheNavigation: "Accès et dépôt",
    resume: "Vue d'ensemble de votre session, du catalogue chargé et de l'état Git.",
    objectif:
      "Vérifier que vous êtes dans le bon périmètre AlphaTools avant de modifier la bibliothèque.",
    utilisation: [
      "Contrôlez votre niveau d'accès et les applications autorisées.",
      "Regardez le nombre de réglages, composants et profils disponibles.",
      "Vérifiez l'état Git avant toute modification appliquée."
    ],
    impact: "Lecture seule. Cet écran ne modifie ni charte, ni composants, ni brouillons.",
    actionPrincipale: "Rafraîchir recharge les données du Studio et l'état Git.",
    exemples: [
      {
        titre: "Avant de changer une couleur",
        situation: "Vous voulez modifier le bleu principal du design system.",
        action: "Vérifiez d'abord que le dépôt est propre et que vous êtes bien en admin suite.",
        resultat:
          "Vous savez si vous pouvez modifier la charte ou si un autre changement attend déjà."
      },
      {
        titre: "Après un enregistrement",
        situation: "Vous venez de sauvegarder un profil ou un brouillon.",
        action: "Cliquez sur Rafraîchir pour relire les données et l'état Git.",
        resultat: "Les compteurs et l'état du dépôt reflètent la situation actuelle."
      },
      {
        titre: "Accès limité",
        situation: "Vous n'êtes pas admin suite mais rattaché à une application.",
        action: "Contrôlez les organisations et codes application affichés.",
        resultat: "Vous savez si vous agissez sur le bon périmètre AlphaTools."
      }
    ]
  },
  {
    id: "atelier",
    libelle: "Créer un style",
    icone: <SlidersHorizontal size={18} />,
    accrocheNavigation: "Styles visuels",
    resume: "Espace de création pour préparer une direction visuelle complète avec aperçu direct.",
    objectif:
      "Créer ou comparer des profils d'apparence sans modifier immédiatement la charte partagée.",
    utilisation: [
      "Choisissez un profil existant ou démarrez un nouveau profil.",
      "Ajustez police, tailles, couleurs, dégradé, image, arrondis et ombres.",
      "Contrôlez le rendu dans l'aperçu puis enregistrez seulement si la direction est utile."
    ],
    impact:
      "Modifie uniquement les profils Studio stockés dans .alpha/studio-profils. La charte partagée ne change pas ici.",
    actionPrincipale:
      "Enregistrer sauvegarde le profil. Supprimer retire seulement le profil choisi.",
    exemples: [
      {
        titre: "Back-office dense",
        situation: "Vous préparez une interface métier avec beaucoup de tableaux.",
        action: "Utilisez Inter, style dense, texte 15 px, arrondi faible et couleurs sobres.",
        resultat: "Vous obtenez une interface compacte, lisible et adaptée au travail quotidien."
      },
      {
        titre: "Page de présentation",
        situation: "Vous voulez tester une page plus éditoriale ou commerciale.",
        action: "Utilisez un style éditorial, titre plus grand, image et dégradé plus marqué.",
        resultat: "Vous visualisez une direction plus expressive sans modifier la charte partagée."
      },
      {
        titre: "Thème client",
        situation: "Une application doit reprendre une couleur de marque.",
        action: "Changez Action, Accent et Dégradé puis vérifiez le contraste dans l'aperçu.",
        resultat: "Vous voyez si la marque reste utilisable avant toute publication."
      }
    ]
  },
  {
    id: "charte",
    libelle: "Charte graphique",
    icone: <Palette size={18} />,
    accrocheNavigation: "Style visuel",
    resume: "Éditeur local pour modifier une charte graphique complète sans écrire de code.",
    objectif:
      "Modifier les couleurs, la typographie, les espacements, les boutons et les formulaires avec un aperçu immédiat sur de vrais composants.",
    utilisation: [
      "Choisissez une catégorie : couleurs, typographie, géométrie, boutons ou formulaires.",
      "Modifiez les contrôles visuels sans toucher au CSS ou au TSX.",
      "Vérifiez l'aperçu combiné, sauvegardez localement puis téléchargez le fichier de charte quand le résultat est cohérent."
    ],
    impact:
      "Sauvegarde locale navigateur et téléchargement de fichier uniquement. Cet écran ne modifie pas directement Alpha Design System.",
    actionPrincipale:
      "Enregistrer localement restaure la charte au rechargement. Télécharger produit un fichier exploitable plus tard.",
    exemples: [
      {
        titre: "Modifier la couleur principale",
        situation: "Vous voulez tester une nouvelle identité sans publier immédiatement.",
        action: "Ouvrez Couleurs, changez Couleur principale et regardez les boutons de l'aperçu.",
        resultat: "Les actions, badges et états actifs changent immédiatement."
      },
      {
        titre: "Tester une interface plus compacte",
        situation: "Les écrans métier doivent afficher plus d'informations.",
        action: "Ouvrez Espacements et arrondis, réduisez les espacements et la hauteur des contrôles.",
        resultat: "Le formulaire, le tableau et les cartes se densifient sans changer de code."
      },
      {
        titre: "Exporter une charte validée",
        situation: "La direction visuelle doit être reprise dans Alpha Design System.",
        action: "Ouvrez Charger ou télécharger puis téléchargez le fichier de charte.",
        resultat: "Le fichier contient la charte structurée pour une reprise technique ultérieure."
      }
    ]
  },
  {
    id: "tokens",
    libelle: "Réglages avancés",
    icone: <Palette size={18} />,
    accrocheNavigation: "Valeurs système",
    resume: "Éditeur des variables du design system : couleurs, typographie, rayons et surfaces.",
    objectif:
      "Préparer un changement global, le simuler, puis l'appliquer explicitement si le résultat est correct.",
    utilisation: [
      "Sélectionnez un réglage dans la liste ou recherchez par nom.",
      "Choisissez le thème, modifiez la valeur, puis ajoutez-la à la simulation.",
      "Simulez pour vérifier, puis appliquez seulement quand la liste des changements est correcte."
    ],
    impact:
      "Appliquer modifie les fichiers source de la charte technique et régénère les artefacts nécessaires.",
    actionPrincipale:
      "Ajouter reste local à l'écran. Simuler vérifie. Appliquer écrit réellement dans le dépôt.",
    exemples: [
      {
        titre: "Changer le bouton principal",
        situation: "Les boutons doivent passer d'un bleu standard à un bleu plus vif.",
        action: "Préparez couleurs.actionPrincipale en clair avec une valeur comme #1f6feb.",
        resultat: "L'aperçu montre immédiatement l'effet sur les boutons principaux."
      },
      {
        titre: "Adapter le survol",
        situation: "Le bouton devient trop proche de sa couleur normale au survol.",
        action: "Préparez couleurs.actionPrincipaleSurvol avec une valeur plus foncée.",
        resultat: "Le survol reste visible et cohérent avec l'action principale."
      },
      {
        titre: "Tester une police",
        situation: "Vous voulez comparer Inter avec Arial ou Georgia.",
        action: "Préparez typographie.familleTexte puis simulez avant d'appliquer.",
        resultat: "Vous mesurez l'effet global sur la lisibilité sans écrire immédiatement."
      }
    ]
  },
  {
    id: "bibliotheque",
    libelle: "Explorer les exemples",
    icone: <Boxes size={18} />,
    accrocheNavigation: "Composants et usages",
    resume: "Bibliothèque pédagogique des composants, patterns et éléments d'interface.",
    objectif:
      "Comprendre quels blocs existent ou doivent être créés, avec des cas d'usage concrets.",
    utilisation: [
      "Filtrez par famille : actions, formulaires, données, navigation, feedback.",
      "Lisez quand utiliser chaque élément et sur quoi il agit.",
      "Passez au Catalogue réel pour voir les exports effectivement disponibles."
    ],
    impact: "Lecture seule. Cette bibliothèque explique les éléments et ne modifie aucun package.",
    actionPrincipale: "La recherche filtre les fiches d'exemples sans écrire dans le dépôt.",
    exemples: [
      {
        titre: "Choisir un élément de formulaire",
        situation: "Vous devez construire un écran de saisie.",
        action: "Filtrez Formulaire et comparez ChampTexte, Select, Case à cocher et Switch.",
        resultat: "Vous choisissez le bon contrôle selon le type de décision utilisateur."
      },
      {
        titre: "Construire une page de données",
        situation: "Vous devez afficher des déclarations, utilisateurs ou dossiers.",
        action:
          "Filtrez Données et Layout, puis repérez TableauDonnees, Pagination et PanneauDetails.",
        resultat: "Vous assemblez un écran métier complet au lieu d'un composant isolé."
      },
      {
        titre: "Prévoir les états",
        situation: "Une page peut charger, être vide ou échouer.",
        action: "Filtrez Feedback et repérez Alerte, EtatVide, Squelette et MessageErreur.",
        resultat: "L'interface reste compréhensible dans les cas réels."
      }
    ]
  },
  {
    id: "catalogue",
    libelle: "Catalogue réel",
    icone: <Layers3 size={18} />,
    accrocheNavigation: "Composants",
    resume: "Inventaire consultable des composants et patterns disponibles dans la bibliothèque.",
    objectif:
      "Retrouver rapidement un composant, son package, son import et son rôle avant de l'utiliser ailleurs.",
    utilisation: [
      "Recherchez par nom, package ou description.",
      "Lisez l'import exact à copier dans une application.",
      "Utilisez cette page pour contrôler ce qui existe déjà avant de créer un nouveau brouillon."
    ],
    impact: "Lecture seule. Cette page ne modifie aucun fichier du design system.",
    actionPrincipale: "La recherche filtre le catalogue affiché, sans changer les sources.",
    exemples: [
      {
        titre: "Chercher un bouton",
        situation: "Vous devez ajouter une action dans une page AlphaTrack.",
        action: "Recherchez Bouton puis copiez l'import affiché.",
        resultat: "Vous réutilisez le composant existant au lieu d'en créer un autre."
      },
      {
        titre: "Trouver un tableau",
        situation: "Vous devez afficher une liste de déclarations ou d'utilisateurs.",
        action: "Recherchez Tableau ou données dans le catalogue.",
        resultat: "Vous voyez le pattern prévu et son package."
      },
      {
        titre: "Contrôler avant import",
        situation: "Vous avez une idée de nouveau composant.",
        action: "Recherchez son nom ou son usage avant d'aller dans Import.",
        resultat: "Vous évitez les doublons dans la bibliothèque."
      }
    ]
  },
  {
    id: "composant",
    libelle: "Composants",
    icone: <Settings size={18} />,
    accrocheNavigation: "Boutons, champs et blocs",
    resume: "Modifiez graphiquement un composant, avec la même logique que la charte.",
    objectif:
      "Choisir un composant du catalogue, modifier son contenu, son état et sa présentation depuis des réglages visuels, puis sauvegarder une variante en brouillon.",
    utilisation: [
      "Sélectionnez un composant réel dans la liste.",
      "Modifiez ses textes, son état, sa tonalité, ses dimensions et ses couleurs dans les réglages graphiques.",
      "Vérifiez l'aperçu du composant seul, puis enregistrez le brouillon de variante."
    ],
    impact:
      "Enregistrer modifie uniquement .alpha/studio-brouillons. Aucun code source de composant n'est modifié automatiquement dans cette étape.",
    actionPrincipale:
      "Les réglages modifient l'aperçu du composant sélectionné. Le code généré reste une option repliée.",
    exemples: [
      {
        titre: "Modifier une navigation",
        situation: "Vous voulez adapter une barre latérale publiée depuis le Studio.",
        action: "Sélectionnez BarreLateraleModerne1, changez le titre, la description et l'écran actif.",
        resultat: "Vous voyez la variante sans toucher au TSX."
      },
      {
        titre: "Préparer un bouton",
        situation: "Vous devez tester un bouton primaire, secondaire ou désactivé.",
        action: "Sélectionnez Bouton, changez le texte, la variante, la taille et l'état.",
        resultat: "Le rendu du bouton est contrôlé graphiquement."
      },
      {
        titre: "Sauvegarder une variante",
        situation: "Le réglage graphique vous convient mais ne doit pas encore être publié.",
        action: "Nommez la variante puis utilisez Enregistrer brouillon.",
        resultat: "Le travail reste dans les brouillons Studio pour reprise ou validation."
      }
    ]
  },
  {
    id: "applications",
    libelle: "Applications",
    icone: <AppWindow size={18} />,
    accrocheNavigation: "Suivi de la charte",
    resume: "Vérifiez où la charte est appliquée et consultez l’historique partagé.",
    objectif: "Suivre les applications Alpha reliées à la charte.",
    utilisation: [],
    impact: "La vérification consulte les applications sans les modifier.",
    actionPrincipale: "Vérifier confirme la disponibilité et la version de charte utilisée.",
    exemples: [],
  },
  {
    id: "import",
    libelle: "Importer",
    icone: <FileCode2 size={18} />,
    accrocheNavigation: "Brouillons",
    resume: "Zone d'entrée pour déposer du code, du texte ou une URL en brouillon contrôlé.",
    objectif:
      "Analyser une proposition avant de la garder en brouillon, puis publier explicitement les brouillons de code validés.",
    utilisation: [
      "Choisissez le mode d'entrée, nommez le brouillon et collez la source.",
      "Analysez pour détecter les composants, avertissements et erreurs.",
      "Enregistrez seulement les brouillons à reprendre, ou ouvrez un brouillon existant pour le modifier.",
      "Utilisez Créer composant @alpha/ui uniquement quand le brouillon doit devenir un export réel."
    ],
    impact:
      "Enregistrer modifie .alpha/studio-brouillons. Créer composant @alpha/ui écrit dans packages/ui/src/studio.",
    actionPrincipale:
      "Analyser ne sauvegarde rien. Enregistrer crée ou remplace un brouillon. Créer composant @alpha/ui publie un export contrôlé.",
    exemples: [
      {
        titre: "Importer du code React",
        situation: "Vous avez déjà un composant écrit dans une autre application.",
        action: "Choisissez Code, collez le composant, lancez Analyser, puis enregistrez.",
        resultat:
          "Le Studio détecte les composants, garde le contenu en brouillon, puis peut créer un composant @alpha/ui sur demande."
      },
      {
        titre: "Décrire un besoin",
        situation: "Vous n'avez pas encore le code, seulement une demande fonctionnelle.",
        action: "Choisissez Texte et décrivez les états, actions et contraintes.",
        resultat: "Le besoin est conservé comme brouillon de conception."
      },
      {
        titre: "Analyser une URL",
        situation: "Une maquette ou une source distante doit être inspectée.",
        action:
          "Choisissez URL, saisissez l'adresse HTTPS puis analysez si l'option serveur est active.",
        resultat: "Le Studio signale ce qu'il peut lire ou bloque l'URL si elle est risquée."
      }
    ]
  },
  {
    id: "graphique",
    libelle: "Éditeur graphique",
    icone: <MousePointer2 size={18} />,
    accrocheNavigation: "No-code visuel",
    resume: "Zone de création sélectionnable avec bibliothèque, organisation et panneau Propriétés.",
    objectif:
      "Composer une interface sans écrire de code, modifier chaque élément graphiquement et sauvegarder une scène extensible.",
    utilisation: [
      "Ajoutez un élément depuis la bibliothèque ou déposez-le sur la zone de travail.",
      "Cliquez sur un bloc pour le sélectionner, puis modifiez texte, couleurs, largeur, espacement et arrondi.",
      "Contrôlez mobile, tablette et desktop, puis sauvegardez le brouillon ou copiez l'export React."
    ],
    impact:
      "Enregistrer modifie uniquement .alpha/studio-brouillons avec une scène JSON versionnée et le code React généré.",
    actionPrincipale:
      "Les actions graphiques changent la scène locale. Enregistrer écrit le brouillon V3, sans publier dans @alpha/ui.",
    exemples: [
      {
        titre: "Créer une carte sans code",
        situation: "Vous voulez maquetter une carte action sans écrire de TSX.",
        action:
          "Ajoutez Carte, Badge, Titre, Texte et Bouton, puis modifiez les libellés dans Propriétés.",
        resultat: "Vous obtenez une composition visuelle et un export React."
      },
      {
        titre: "Tester le responsive",
        situation: "Vous devez vérifier une maquette en mobile avant de la reprendre.",
        action: "Utilisez les boutons Mobile, Tablette et Desktop de la zone de travail.",
        resultat: "La composition est contrôlée sur plusieurs largeurs sans quitter l'écran."
      },
      {
        titre: "Préparer la suite",
        situation: "Le besoin va évoluer avec plus de composants et de règles.",
        action: "Sauvegardez un brouillon graphique V3 pour conserver la scène JSON.",
        resultat: "La maquette peut être rechargée et enrichie plus tard."
      }
    ]
  },
  {
    id: "live",
    libelle: "Éditeur Live",
    icone: <Code2 size={18} />,
    accrocheNavigation: "React en direct",
    resume: "Éditeur React TSX avec Monaco, propriétés locales et prévisualisation isolée.",
    objectif:
      "Modifier un composant en direct, voir le rendu immédiatement et sauvegarder le résultat en brouillon contrôlé.",
    utilisation: [
      "Chargez un exemple ou modifiez le TSX dans l'éditeur Monaco.",
      "Ajustez les propriétés JSON, la taille d'écran et le thème pour tester plusieurs états.",
      "Lisez les erreurs de compilation ou d'exécution avant d'enregistrer le brouillon."
    ],
    impact:
      "Enregistrer modifie uniquement .alpha/studio-brouillons. La preview s'exécute côté navigateur dans une iframe isolée.",
    actionPrincipale:
      "Lancer recompile la source localement. Enregistrer brouillon conserve le code et les propriétés de prévisualisation.",
    exemples: [
      {
        titre: "Modifier une carte",
        situation: "Vous voulez ajuster textes, badge et action d'un composant simple.",
        action: "Chargez Carte action, modifiez Props JSON puis observez l'aperçu.",
        resultat: "Le rendu change sans toucher aux packages publiés."
      },
      {
        titre: "Tester une sidebar",
        situation: "Vous voulez vérifier la sidebar moderne en contexte application.",
        action: "Chargez Sidebar live, changez le produit et la taille Mobile ou Desktop.",
        resultat: "Vous voyez le comportement responsive dans l'iframe."
      },
      {
        titre: "Préparer un composant",
        situation: "Le rendu est bon et doit être repris plus tard dans @alpha/ui.",
        action: "Enregistrez le brouillon live avec un nom explicite.",
        resultat: "Le code reste traçable avant intégration manuelle et validation."
      }
    ]
  },
  {
    id: "validation",
    libelle: "Valider",
    icone: <Play size={18} />,
    accrocheNavigation: "Tests serveur",
    resume: "Lancement authentifié des commandes de contrôle autorisées sur le serveur.",
    objectif:
      "Vérifier que les changements restent cohérents avant de les livrer ou de les promouvoir.",
    utilisation: [
      "Sélectionnez une commande selon le niveau de vérification voulu.",
      "Lancez-la depuis le serveur Studio authentifié.",
      "Lisez la sortie et corrigez les erreurs avant toute livraison."
    ],
    impact:
      "Exécute uniquement une commande en liste blanche : catalogue, tests, types, build ou vérification complète.",
    actionPrincipale:
      "Lancer exécute la commande choisie et affiche sa sortie. Aucune migration ni donnée DEV n'est touchée.",
    exemples: [
      {
        titre: "Après un import",
        situation: "Vous avez créé ou modifié un brouillon de composant.",
        action: "Lancez Catalogue puis Tests.",
        resultat: "Vous vérifiez que l'inventaire et les tests restent cohérents."
      },
      {
        titre: "Après une modification TypeScript",
        situation: "Un contrat de composant ou une prop a changé.",
        action: "Lancez Types puis Build.",
        resultat: "Vous détectez les erreurs de compilation avant livraison."
      },
      {
        titre: "Avant de clôturer",
        situation: "Vous pensez que l'application est prête.",
        action: "Lancez Vérification complète.",
        resultat: "Vous obtenez la recette la plus large disponible côté serveur."
      }
    ]
  }
];

const exemplesLectureControle: ExempleSimple[] = [
  {
    titre: "Dépôt propre",
    description: "Le statut Git affiche Aucun changement détecté.",
    details: [
      "Vous pouvez préparer une simulation de réglages.",
      "Vous pouvez comparer des profils d'apparence.",
      "Vous savez que les prochains changements visibles viendront de votre action."
    ]
  },
  {
    titre: "Dépôt modifié",
    description: "Le statut Git affiche une ou plusieurs lignes.",
    details: [
      "Lisez les fichiers concernés avant d'appliquer des réglages.",
      "Évitez de mélanger une correction en cours avec un essai visuel.",
      "Lancez une validation uniquement si vous comprenez les changements affichés."
    ]
  },
  {
    titre: "Admin suite",
    description: "Le badge Suite indique un accès large via AlphaTools.",
    details: [
      "Vous pouvez contrôler l'ensemble de la bibliothèque.",
      "Les actions persistantes doivent être vérifiées avec plus de prudence.",
      "L'écran reste lié à votre session AlphaTools."
    ]
  },
  {
    titre: "Accès application",
    description: "Le badge Application indique un accès limité au périmètre autorisé.",
    details: [
      "Contrôlez les organisations listées.",
      "Vérifiez le code ALPHA_DESIGN_STUDIO.",
      "Ne considérez pas l'accès comme global à toute la suite."
    ]
  }
];

const exemplesProfils: ExempleProfil[] = [
  {
    titre: "Profil back-office dense",
    description: "Pour AlphaTrack, tableaux, listes et écrans utilisés tous les jours.",
    profil: {
      ...profilApparenceInitial,
      nom: "Profil back-office dense",
      description: "Interface compacte pour écrans métier riches en données.",
      tailleBase: 15,
      tailleTitre: 24,
      styleCarte: "dense",
      couleurPrincipale: "#0b5cab",
      couleurSurface: "#ffffff",
      couleurTexte: "#15202b",
      couleurAccent: "#e8f2ff",
      gradientDepart: "#f7f9fb",
      gradientArrivee: "#dbeafe",
      angleDegres: 90,
      arrondi: 6,
      intensiteOmbre: 8
    },
    details: [
      "À utiliser pour les dashboards et tableaux.",
      "Peu d'ombre, arrondi faible, lecture rapide.",
      "Bon point de départ pour une application interne."
    ]
  },
  {
    titre: "Profil portail client",
    description: "Pour un espace plus aéré avec des cartes et actions visibles.",
    profil: {
      ...profilApparenceInitial,
      nom: "Profil portail client",
      description: "Interface claire pour consultation client et parcours guidé.",
      tailleBase: 16,
      tailleTitre: 30,
      styleCarte: "equilibre",
      couleurPrincipale: "#0067b8",
      couleurSurface: "#ffffff",
      couleurTexte: "#15202b",
      couleurAccent: "#eef6ff",
      gradientDepart: "#e8f2ff",
      gradientArrivee: "#dff7ea",
      angleDegres: 135,
      arrondi: 10,
      intensiteOmbre: 14
    },
    details: [
      "À utiliser pour une interface guidée.",
      "Équilibre entre densité et respiration.",
      "Les boutons restent très visibles."
    ]
  },
  {
    titre: "Profil éditorial",
    description: "Pour tester une page plus expressive avec grand titre et image.",
    profil: {
      ...profilApparenceInitial,
      nom: "Profil éditorial",
      description: "Présentation visuelle pour page d'accueil, vitrine ou documentation.",
      famillePolice: "Georgia, serif",
      tailleBase: 18,
      tailleTitre: 38,
      graisseTitre: 700,
      styleCarte: "editorial",
      couleurPrincipale: "#7c2d12",
      couleurSurface: "#fffaf2",
      couleurTexte: "#1f2933",
      couleurAccent: "#f7e4c6",
      gradientDepart: "#fff7ed",
      gradientArrivee: "#dbeafe",
      angleDegres: 120,
      arrondi: 16,
      intensiteOmbre: 22
    },
    details: [
      "À utiliser pour explorer une direction plus marquée.",
      "Grand titre et rythme plus large.",
      "À valider soigneusement avant de l'appliquer à la charte."
    ]
  }
];

const exemplesTokens: ExempleToken[] = [
  {
    titre: "Réglage action principale",
    chemin: "couleurs.actionPrincipale",
    theme: "clair",
    valeur: "#1f6feb",
    description: "Change la couleur des boutons principaux et actions prioritaires.",
    details: [
      "Exemple : bouton Enregistrer plus visible.",
      "Format attendu : couleur hexadécimale #RRGGBB.",
      "À simuler avant application pour contrôler le contraste."
    ]
  },
  {
    titre: "Réglage survol action",
    chemin: "couleurs.actionPrincipaleSurvol",
    theme: "clair",
    valeur: "#185abc",
    description: "Change la couleur au passage de la souris sur une action principale.",
    details: [
      "Exemple : bouton qui fonce au survol.",
      "La valeur doit rester proche de l'action principale.",
      "À ajuster avec la couleur d'action principale."
    ]
  },
  {
    titre: "Réglage surface accentuée",
    chemin: "couleurs.fondSurfaceAccent",
    theme: "clair",
    valeur: "#eef6ff",
    description: "Change les fonds de zones secondaires, encarts et repères doux.",
    details: [
      "Exemple : bloc d'information légèrement bleuté.",
      "Utile pour guider sans créer une alerte.",
      "Doit rester assez clair pour préserver la lecture."
    ]
  },
  {
    titre: "Réglage police globale",
    chemin: "typographie.familleTexte",
    theme: "clair",
    valeur: "Inter, ui-sans-serif, system-ui, sans-serif",
    description: "Change la famille typographique principale des interfaces.",
    details: [
      "Exemple : Inter pour une application métier.",
      "Exemple : Arial pour une compatibilité très large.",
      "À appliquer seulement après vérification mobile et desktop."
    ]
  },
  {
    titre: "Réglage densité standard",
    chemin: "densites.standard",
    theme: "clair",
    valeur: "1",
    description: "Change la densité générale des espacements pilotés par densité.",
    details: [
      "Exemple : 0.9 pour une interface plus compacte.",
      "Exemple : 1.1 pour une interface plus respirante.",
      "À tester sur tableaux et formulaires avant application."
    ]
  }
];

const exemplesCatalogue: ExempleSimple[] = [
  {
    titre: "Recherche Bouton",
    description: "Tapez Bouton pour retrouver l'action standard.",
    details: [
      "Vous voyez le package @alpha/ui.",
      "Vous copiez l'import exact.",
      "Vous évitez de recréer un bouton local dans une application."
    ]
  },
  {
    titre: "Recherche Tableau",
    description: "Tapez Tableau ou données pour trouver le pattern de liste.",
    details: [
      "Utile pour les utilisateurs, déclarations ou lignes de suivi.",
      "Le package indique si c'est un composant ou un pattern.",
      "La description précise le rôle prévu."
    ]
  },
  {
    titre: "Recherche Alerte",
    description: "Tapez Alerte pour retrouver les messages d'information ou d'erreur.",
    details: [
      "Permet d'afficher une erreur métier.",
      "Permet d'afficher une information non bloquante.",
      "Évite les styles d'alerte bricolés dans chaque application."
    ]
  }
];

const exempleComposant = `import { Bouton, Carte } from "@alpha/ui";

export function CarteAction() {
  return (
    <Carte>
      <h2>Action</h2>
      <Bouton>Valider</Bouton>
    </Carte>
  );
}
`;

const exempleBarreLaterale = `"use client";
import React, { useState } from "react";
import {
  Home,
  BarChart3,
  FileText,
  Bell,
  User,
  Settings,
  HelpCircle
} from "lucide-react";

interface NavigationItem {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  badge?: string;
}

const navigationItems: NavigationItem[] = [
  { id: "dashboard", name: "Dashboard", icon: Home, href: "/dashboard" },
  { id: "analytics", name: "Analytics", icon: BarChart3, href: "/analytics" },
  { id: "documents", name: "Documents", icon: FileText, href: "/documents", badge: "3" },
  { id: "notifications", name: "Notifications", icon: Bell, href: "/notifications", badge: "12" },
  { id: "profile", name: "Profile", icon: User, href: "/profile" },
  { id: "settings", name: "Settings", icon: Settings, href: "/settings" },
  { id: "help", name: "Help & Support", icon: HelpCircle, href: "/help" }
];

export function Sidebar() {
  const [activeItem, setActiveItem] = useState("dashboard");

  return (
    <aside className="fixed top-0 left-0 h-full w-78 bg-white border-r border-slate-200">
      {navigationItems.map((item) => (
        <button
          className={activeItem === item.id ? "bg-blue-50 text-blue-700" : "text-slate-600"}
          key={item.id}
          onClick={() => setActiveItem(item.id)}
          type="button"
        >
          <item.icon className="h-4.5 w-4.5" />
          {item.name}
          {item.badge}
        </button>
      ))}
    </aside>
  );
}
`;

const exemplesImports: ExempleImportStudio[] = [
  {
    titre: "Exemple code React",
    mode: "code",
    nom: "CarteAction",
    url: "",
    description: "Pour reprendre un composant déjà écrit.",
    details: [
      "Collez du TSX ou JSX.",
      "Analysez pour détecter les composants utilisés.",
      "Enregistrez seulement si le code mérite d'être retravaillé."
    ],
    contenu: exempleComposant
  },
  {
    titre: "Exemple sidebar moderne",
    mode: "code",
    nom: "BarreLateraleModerne",
    url: "",
    description: "Pour transformer un composant shadcn/Tailwind en composant Alpha visible.",
    details: [
      "Le code collé reste un brouillon, il n'est pas exécuté tel quel.",
      "Le Studio détecte Next, Tailwind, lucide-react et la navigation.",
      "L'aperçu montre l'adaptation Alpha réellement disponible dans @alpha/ui."
    ],
    contenu: exempleBarreLaterale
  },
  {
    titre: "Exemple texte de besoin",
    mode: "texte",
    nom: "BesoinTableauSuivi",
    url: "",
    description: "Pour décrire un composant avant d'avoir le code.",
    details: [
      "Décrivez les colonnes attendues.",
      "Listez les actions et états vides.",
      "Gardez le besoin comme brouillon de conception."
    ],
    contenu: `Besoin : créer un tableau de suivi pour une application métier.

Exemples d'états :
- liste chargée avec 20 lignes ;
- liste vide avec action de création ;
- erreur de chargement avec message clair.

Actions attendues :
- filtrer par statut ;
- ouvrir le détail ;
- exporter la liste.`
  },
  {
    titre: "Exemple URL HTTPS",
    mode: "url",
    nom: "ReferenceExterne",
    url: "https://exemple.invalid/design/reference-composant",
    description: "Pour noter une source distante à analyser si l'import URL est autorisé.",
    details: [
      "Utilisez uniquement une URL HTTPS.",
      "Les hôtes locaux ou privés sont refusés.",
      "Si l'import URL est désactivé, gardez l'URL en brouillon texte."
    ],
    contenu: "Référence distante à analyser lorsque l'import URL est activé côté serveur."
  }
];

const exemplesValidation: ExempleSimple[] = [
  {
    titre: "Commande Catalogue",
    description: "À lancer après ajout ou renommage de composant.",
    details: [
      "Régénère l'inventaire consultable.",
      "Vérifie que le catalogue reflète les sources.",
      "À utiliser avant de chercher le composant dans l'onglet Catalogue."
    ]
  },
  {
    titre: "Commande Tests",
    description: "À lancer après changement de logique ou de transformation.",
    details: [
      "Couvre les tests Vitest du dépôt.",
      "À utiliser après modification de la charte ou du serveur Studio.",
      "Un échec doit être lu dans la sortie avant de continuer."
    ]
  },
  {
    titre: "Commande Types",
    description: "À lancer après changement de props, contrats ou données.",
    details: [
      "Détecte les erreurs TypeScript.",
      "Utile après changement d'API interne.",
      "Plus rapide qu'un build complet."
    ]
  },
  {
    titre: "Commande Build",
    description: "À lancer avant de considérer l'interface livrable.",
    details: [
      "Compile les packages et applications.",
      "Détecte les erreurs de bundling.",
      "À compléter par Playwright quand l'UI change."
    ]
  }
];

const conceptsStudio: ConceptStudio[] = [
  {
    nom: "Design system",
    definition:
      "Un ensemble de règles visuelles, composants et modèles d'interface pour construire des écrans cohérents.",
    utilite:
      "Éviter que chaque application recrée ses boutons, couleurs, formulaires et tableaux à sa manière.",
    impact: "Il agit sur toutes les applications qui consomment les packages Alpha publiés.",
    fonctionnement:
      "Les réglages globaux définissent les décisions visuelles, les composants les utilisent, les modèles assemblent plusieurs composants.",
    exemples: [
      "AlphaTrack réutilise les mêmes boutons et tableaux qu'une autre application Alpha.",
      "Un changement de couleur principale peut se répercuter partout.",
      "Un modèle PageListe donne la même structure aux pages de listes."
    ],
    action: "bibliotheque"
  },
  {
    nom: "Réglage global",
    definition: "Un choix visuel partagé, par exemple une couleur principale, une taille ou un arrondi.",
    utilite: "Changer une décision de design à un seul endroit au lieu de modifier chaque écran.",
    impact:
      "Un réglage appliqué modifie les composants qui l'utilisent.",
    fonctionnement:
      "Vous modifiez la valeur dans l'interface. Le Studio se charge de la structure technique derrière.",
    exemples: [
      "Couleur principale change les boutons importants.",
      "Fond de surface change les cartes et panneaux.",
      "Police globale change la typographie générale."
    ],
    action: "charte"
  },
  {
    nom: "Composant",
    definition:
      "Un élément d'interface réutilisable : bouton, champ, badge, carte, alerte, tableau.",
    utilite: "Construire les écrans avec des blocs fiables, testés et cohérents.",
    impact: "Il agit sur l'écran où il est utilisé, sans changer seul l'identité visuelle globale.",
    fonctionnement:
      "Une application importe le composant depuis @alpha/ui ou @alpha/patterns et lui passe des props. L'éditeur de composant permet de préparer ces props graphiquement.",
    exemples: [
      "Bouton pour Enregistrer ou Annuler.",
      "ChampTexte pour saisir un nom.",
      "Alerte pour afficher une erreur ou un avertissement."
    ],
    action: "composant"
  },
  {
    nom: "Modèle d'interface",
    definition: "Un assemblage de composants pour résoudre un cas récurrent.",
    utilite:
      "Éviter de reconstruire à chaque fois une page de liste, un formulaire en sections ou un détail latéral.",
    impact: "Il structure une zone complète de l'interface, pas seulement un contrôle isolé.",
    fonctionnement:
      "Un modèle combine plusieurs composants et impose une organisation réutilisable.",
    exemples: [
      "PageListe assemble titre, filtres, actions et tableau.",
      "MiseEnPageListeDetails assemble une liste et un panneau de détail.",
      "FormulaireEnSections regroupe des champs par blocs lisibles."
    ],
    action: "bibliotheque"
  },
  {
    nom: "Profil d'apparence",
    definition:
      "Un essai visuel complet pour tester police, tailles, couleurs, dégradé, image et densité.",
    utilite: "Comparer des directions visuelles sans modifier immédiatement la charte partagée.",
    impact: "Il écrit seulement dans .alpha/studio-profils, pas dans la charte partagée.",
    fonctionnement:
      "Vous chargez ou créez un profil, l'aperçu se met à jour, puis Enregistrer conserve l'essai.",
    exemples: [
      "Profil back-office dense pour tableaux métier.",
      "Profil portail client pour une interface plus aérée.",
      "Profil éditorial pour une page plus expressive."
    ],
    action: "atelier"
  },
  {
    nom: "Brouillon",
    definition: "Une source gardée de côté pour analyse ou reprise : code, texte de besoin ou URL.",
    utilite: "Conserver une idée, puis publier explicitement les brouillons de code validés.",
    impact:
      "Il écrit dans .alpha/studio-brouillons. Créer composant @alpha/ui écrit dans packages/ui/src/studio.",
    fonctionnement:
      "Analyser lit la source, Enregistrer la conserve, Ouvrir la remet dans l'éditeur, Créer composant @alpha/ui la publie.",
    exemples: [
      "Un composant React copié depuis une application.",
      "Une description de besoin sans code.",
      "Une URL HTTPS de référence à analyser."
    ],
    action: "import"
  },
  {
    nom: "Catalogue réel",
    definition: "La liste des composants et modèles actuellement disponibles dans les packages Alpha.",
    utilite: "Savoir ce qui existe vraiment avant de créer ou importer autre chose.",
    impact: "Lecture seule : le catalogue ne modifie rien.",
    fonctionnement:
      "Le catalogue est généré à partir de la configuration du dépôt et affiché dans Studio.",
    exemples: [
      "Voir que Bouton vient de @alpha/ui.",
      "Voir que TableauDonnees vient de @alpha/patterns.",
      "Copier l'import exact dans une application."
    ],
    action: "catalogue"
  },
  {
    nom: "Simulation",
    definition: "Une vérification temporaire avant d'écrire dans le dépôt.",
    utilite: "Contrôler les erreurs et l'aperçu avant une action persistante.",
    impact: "Tant que vous simulez, aucun fichier source n'est modifié.",
    fonctionnement:
      "Vous ajoutez des changements à la liste, puis Simuler envoie ces valeurs au serveur sans écriture finale.",
    exemples: [
      "Tester #1f6feb sur le bouton principal.",
      "Vérifier qu'une valeur de couleur est au format #RRGGBB.",
      "Contrôler l'aperçu avant Appliquer."
    ],
    action: "charte"
  },
  {
    nom: "Éditeur graphique",
    definition:
      "Une zone de travail no-code où chaque élément est sélectionnable et modifiable depuis le panneau Propriétés.",
    utilite:
      "Composer une interface sans écrire de code, puis récupérer une scène sauvegardée ou un export React.",
    impact:
      "Il agit sur une scène locale et peut enregistrer un brouillon V3 ; il ne publie pas automatiquement de composant.",
    fonctionnement:
      "La bibliothèque ajoute des éléments à la zone de travail, la liste permet de sélectionner, le panneau Propriétés modifie le rendu, puis l'export React est généré.",
    exemples: [
      "Ajouter une carte puis changer son titre.",
      "Modifier couleur, arrondi et espacement depuis le panneau Propriétés.",
      "Sauvegarder une composition graphique pour la reprendre plus tard."
    ],
    action: "graphique"
  },
  {
    nom: "Éditeur live",
    definition:
      "Un espace avancé où le code React, les paramètres et les réglages locaux sont prévisualisés immédiatement.",
    utilite:
      "Comprendre et ajuster un composant sans attendre une intégration manuelle dans packages/ui.",
    impact:
      "Il agit dans le navigateur et peut enregistrer un brouillon, mais ne publie pas un composant.",
    fonctionnement:
      "Monaco édite le code, TypeScript le compile côté navigateur, puis l'iframe rend le composant avec les modules Alpha autorisés.",
    exemples: [
      "Modifier le titre d'une carte dans Props JSON.",
      "Tester une sidebar en mobile.",
      "Préparer un tableau avant de le reprendre dans @alpha/patterns."
    ],
    action: "live"
  },
  {
    nom: "Validation",
    definition: "Une commande contrôlée lancée côté serveur pour vérifier le dépôt.",
    utilite: "Détecter les erreurs avant de considérer un changement livrable.",
    impact:
      "Elle exécute une commande autorisée ; elle ne touche pas aux données DEV ni aux migrations.",
    fonctionnement:
      "Vous choisissez catalogue, tests, types, build ou vérification complète, puis vous lisez la sortie.",
    exemples: [
      "Types après changement de contrat TypeScript.",
      "Build avant déploiement.",
      "Vérification complète avant clôture."
    ],
    action: "validation"
  },
  {
    nom: "Git",
    definition: "L'état des fichiers du dépôt : propres, modifiés, ajoutés ou supprimés.",
    utilite: "Savoir si une action va s'ajouter à des changements déjà présents.",
    impact:
      "Lecture seule dans Contrôler ; les actions Studio peuvent ensuite créer des changements Git.",
    fonctionnement:
      "Le Studio lit la branche, le commit et le status court pour signaler les modifications.",
    exemples: [
      "Aucun changement détecté : point de départ clair.",
      "Fichiers de charte modifiés : un réglage a probablement été appliqué.",
      ".alpha/studio-brouillons modifié : un brouillon a été enregistré."
    ],
    action: "controle"
  },
  {
    nom: "AlphaTools",
    definition: "Le socle d'authentification, comptes, organisations et droits.",
    utilite: "Éviter une gestion d'utilisateurs locale dans Studio.",
    impact: "Il détermine qui peut accéder au Studio et avec quel périmètre.",
    fonctionnement:
      "Studio relaie la connexion, vérifie le JWT AlphaTools et résout les droits d'application.",
    exemples: [
      "MFA demandé après mot de passe.",
      "Admin suite avec accès global.",
      "Accès application limité à ALPHA_DESIGN_STUDIO."
    ],
    action: "controle"
  }
];

const parcoursStudio: ParcoursStudio[] = [
  {
    titre: "Construire une interface sans code",
    objectif: "Composer une maquette visuelle avec une zone de travail et un panneau Propriétés.",
    depart: "graphique",
    etapes: [
      "Ouvrir Éditeur graphique.",
      "Ajouter des éléments depuis la bibliothèque.",
      "Sélectionner chaque bloc dans la zone de travail ou la liste des éléments.",
      "Modifier textes, couleurs, largeur, espacement et arrondi.",
      "Sauvegarder un brouillon V3 ou copier l'export React."
    ],
    resultat: "Vous obtenez une composition graphique modifiable sans écrire directement de code."
  },
  {
    titre: "Modifier un composant en direct",
    objectif: "Tester code, props et rendu avant de créer un composant public.",
    depart: "live",
    etapes: [
      "Ouvrir Éditeur Live.",
      "Charger un exemple proche du besoin.",
      "Modifier le TSX ou les propriétés JSON.",
      "Tester mobile, tablette et desktop.",
      "Enregistrer un brouillon si le rendu doit être repris."
    ],
    resultat:
      "Vous obtenez un prototype React vérifiable sans écrire automatiquement dans les packages."
  },
  {
    titre: "Changer une couleur utilisée partout",
    objectif: "Modifier une décision visuelle globale sans casser la cohérence.",
    depart: "charte",
    etapes: [
      "Ouvrir Charte graphique.",
      "Sélectionner Couleurs.",
      "Modifier la couleur principale avec le sélecteur visuel.",
      "Vérifier immédiatement l'aperçu.",
      "Sauvegarder ou télécharger la charte quand le résultat est correct."
    ],
    resultat: "La charte est mise à jour sans manipuler directement les fichiers techniques."
  },
  {
    titre: "Tester une direction graphique sans publier",
    objectif: "Comparer une ambiance visuelle avant de modifier la charte partagée.",
    depart: "atelier",
    etapes: [
      "Lire le concept Profil d'apparence.",
      "Ouvrir Créer un style.",
      "Charger un profil d'exemple.",
      "Ajuster les couleurs, tailles et arrondis.",
      "Enregistrer le profil si l'essai est utile."
    ],
    resultat: "Un profil est conservé dans Studio, sans modifier les packages publiés."
  },
  {
    titre: "Ajouter une idée de composant",
    objectif: "Conserver une proposition sans polluer directement la bibliothèque.",
    depart: "import",
    etapes: [
      "Chercher d'abord dans Explorer les exemples et Catalogue réel.",
      "Ouvrir Importer.",
      "Choisir Code, Texte ou URL.",
      "Analyser la source.",
      "Enregistrer en brouillon si elle mérite d'être reprise."
    ],
    resultat:
      "La proposition existe en brouillon contrôlé ; un brouillon Code peut ensuite devenir un composant @alpha/ui explicite."
  },
  {
    titre: "Construire une page métier",
    objectif: "Choisir les bons blocs avant d'écrire du code applicatif.",
    depart: "bibliotheque",
    etapes: [
      "Explorer les familles Layout, Données, Formulaire et Feedback.",
      "Repérer les composants disponibles.",
      "Vérifier les imports dans Catalogue réel.",
      "Assembler un pattern plutôt que des éléments isolés."
    ],
    resultat: "La page est pensée comme un assemblage cohérent."
  },
  {
    titre: "Vérifier avant livraison",
    objectif: "Contrôler que le dépôt reste cohérent après changement.",
    depart: "validation",
    etapes: [
      "Ouvrir Contrôler et lire l'état Git.",
      "Choisir la commande adaptée dans Valider.",
      "Lire la sortie.",
      "Corriger avant de considérer le travail terminé."
    ],
    resultat: "Les erreurs de types, tests ou build sont détectées avant livraison."
  }
];

const elementsBibliotheque: ElementBibliotheque[] = [
  {
    nom: "Bouton",
    famille: "Actions",
    statut: "Disponible",
    sertA: "Déclencher une action explicite.",
    quandUtiliser: ["Enregistrer", "Annuler", "Lancer une validation"],
    agitSur: "L'action liée au bouton, pas sur le style global.",
    exemples: ["Bouton primaire pour Enregistrer", "Bouton secondaire pour Annuler"],
    import: 'import { Bouton } from "@alpha/ui";'
  },
  {
    nom: "BoutonIcone",
    famille: "Actions",
    statut: "Disponible",
    sertA: "Déclencher une action compacte avec une icône et un libellé accessible.",
    quandUtiliser: ["Rafraîchir", "Supprimer une ligne", "Ouvrir un menu"],
    agitSur: "Une action ponctuelle dans une barre d'outils.",
    exemples: ["Rafraîchir une page", "Supprimer un brouillon"],
    import: 'import { BoutonIcone } from "@alpha/ui";'
  },
  {
    nom: "GroupeActions",
    famille: "Actions",
    statut: "Disponible",
    sertA: "Aligner plusieurs actions liées.",
    quandUtiliser: ["Enregistrer + Supprimer", "Précédent + Suivant"],
    agitSur: "La disposition des commandes.",
    exemples: ["Actions de formulaire", "Actions de ligne"],
    import: 'import { GroupeActions } from "@alpha/ui";'
  },
  {
    nom: "ChampTexte",
    famille: "Formulaire",
    statut: "Disponible",
    sertA: "Saisir une valeur texte.",
    quandUtiliser: ["Nom", "Email", "Code court"],
    agitSur: "La donnée saisie par l'utilisateur.",
    exemples: ["Nom de profil", "Recherche catalogue"],
    import: 'import { ChampTexte } from "@alpha/ui";'
  },
  {
    nom: "ChampNombre",
    famille: "Formulaire",
    statut: "Disponible",
    sertA: "Saisir une valeur numérique.",
    quandUtiliser: ["Quantité", "Ordre", "Seuil"],
    agitSur: "Une donnée numérique contrôlée.",
    exemples: ["Nombre de lignes", "Priorité"],
    import: 'import { ChampNombre } from "@alpha/ui";'
  },
  {
    nom: "ZoneTexte",
    famille: "Formulaire",
    statut: "Disponible",
    sertA: "Saisir un contenu long.",
    quandUtiliser: ["Description", "Code", "Commentaire"],
    agitSur: "Une donnée longue ou multi-ligne.",
    exemples: ["Source d'import", "Description de besoin"],
    import: 'import { ZoneTexte } from "@alpha/ui";'
  },
  {
    nom: "ListeDeroulante",
    famille: "Formulaire",
    statut: "Disponible",
    sertA: "Choisir une option dans une liste fermée.",
    quandUtiliser: ["Thème", "Statut", "Mode d'import"],
    agitSur: "Une valeur unique sélectionnée.",
    exemples: ["Clair / Sombre / Contraste", "Code / Texte / URL"],
    import: 'import { ListeDeroulante } from "@alpha/ui";'
  },
  {
    nom: "CaseACocher",
    famille: "Formulaire",
    statut: "Disponible",
    sertA: "Activer une option indépendante.",
    quandUtiliser: ["Inclure archives", "Accepter une condition"],
    agitSur: "Une valeur booléenne.",
    exemples: ["Afficher les éléments inactifs", "Recevoir une notification"],
    import: 'import { CaseACocher } from "@alpha/ui";'
  },
  {
    nom: "GroupeBoutonsRadio",
    famille: "Formulaire",
    statut: "Disponible",
    sertA: "Choisir une seule option parmi plusieurs visibles.",
    quandUtiliser: ["Mode exclusif", "Priorité", "Type de vue"],
    agitSur: "Une décision unique.",
    exemples: ["Vue liste ou grille", "Criticité faible/moyenne/forte"],
    import: 'import { GroupeBoutonsRadio } from "@alpha/ui";'
  },
  {
    nom: "Interrupteur",
    famille: "Formulaire",
    statut: "Disponible",
    sertA: "Activer ou désactiver une option immédiate.",
    quandUtiliser: ["Mode actif", "Option serveur authentifiée"],
    agitSur: "Un état booléen visible.",
    exemples: ["Activer une règle", "Afficher un mode avancé"],
    import: 'import { Interrupteur } from "@alpha/ui";'
  },
  {
    nom: "Carte",
    famille: "Layout",
    statut: "Disponible",
    sertA: "Regrouper un sujet ou une action courte.",
    quandUtiliser: ["Indicateur", "Exemple", "Résumé"],
    agitSur: "La lisibilité d'un bloc de contenu.",
    exemples: ["Carte d'exemple", "Carte indicateur"],
    import: 'import { Carte } from "@alpha/ui";'
  },
  {
    nom: "Panneau",
    famille: "Layout",
    statut: "Disponible",
    sertA: "Structurer une section complète.",
    quandUtiliser: ["Formulaire", "Aperçu", "Sortie de validation"],
    agitSur: "L'organisation de la page.",
    exemples: ["Panneau Réglages", "Panneau Sortie"],
    import: 'import { Panneau } from "@alpha/ui";'
  },
  {
    nom: "GrilleResponsive",
    famille: "Layout",
    statut: "Disponible",
    sertA: "Afficher des cartes qui s'adaptent à la largeur.",
    quandUtiliser: ["Bibliothèque", "Indicateurs", "Galerie"],
    agitSur: "La disposition responsive.",
    exemples: ["Grille d'exemples", "Grille d'indicateurs"],
    import: 'import { GrilleResponsive } from "@alpha/ui";'
  },
  {
    nom: "Empilement",
    famille: "Layout",
    statut: "Disponible",
    sertA: "Empiler des éléments avec un espacement cohérent.",
    quandUtiliser: ["Formulaire simple", "Liste de messages"],
    agitSur: "Le rythme vertical.",
    exemples: ["Champs empilés", "Cartes empilées"],
    import: 'import { Empilement } from "@alpha/ui";'
  },
  {
    nom: "Badge",
    famille: "Feedback",
    statut: "Disponible",
    sertA: "Afficher un statut court.",
    quandUtiliser: ["Stable", "Erreur", "Information", "Succès"],
    agitSur: "La compréhension rapide d'un état.",
    exemples: ["Badge Suite", "Badge Stable"],
    import: 'import { Badge } from "@alpha/ui";'
  },
  {
    nom: "Alerte",
    famille: "Feedback",
    statut: "Disponible",
    sertA: "Afficher une information importante dans le contexte.",
    quandUtiliser: ["Erreur API", "Avertissement import URL", "Information MFA"],
    agitSur: "La compréhension d'un événement ou risque.",
    exemples: ["AlphaTools indisponible", "URL refusée"],
    import: 'import { Alerte } from "@alpha/ui";'
  },
  {
    nom: "MessageErreur",
    famille: "Feedback",
    statut: "Disponible",
    sertA: "Afficher une erreur proche du formulaire.",
    quandUtiliser: ["Connexion impossible", "Enregistrement refusé"],
    agitSur: "La correction immédiate par l'utilisateur.",
    exemples: ["Mot de passe requis", "Code MFA invalide"],
    import: 'import { MessageErreur } from "@alpha/ui";'
  },
  {
    nom: "EtatVide",
    famille: "Feedback",
    statut: "Disponible",
    sertA: "Expliquer une zone sans contenu.",
    quandUtiliser: ["Aucun brouillon", "Aucun résultat"],
    agitSur: "La compréhension d'une liste vide.",
    exemples: ["Créer un premier profil", "Aucun composant trouvé"],
    import: 'import { EtatVide } from "@alpha/ui";'
  },
  {
    nom: "Squelette",
    famille: "Feedback",
    statut: "Disponible",
    sertA: "Représenter une zone en chargement.",
    quandUtiliser: ["Chargement réseau", "Données lentes"],
    agitSur: "La perception d'attente.",
    exemples: ["Lignes de tableau en attente", "Carte en attente"],
    import: 'import { Squelette } from "@alpha/ui";'
  },
  {
    nom: "Tableau",
    famille: "Données",
    statut: "Disponible",
    sertA: "Afficher des données tabulaires simples.",
    quandUtiliser: ["Liste courte", "Comparaison"],
    agitSur: "La lecture structurée de données.",
    exemples: ["Contexte actif", "Liste de réglages"],
    import: 'import { Tableau } from "@alpha/ui";'
  },
  {
    nom: "TableauDonnees",
    famille: "Données",
    statut: "Pattern",
    sertA: "Afficher un tableau typé avec chargement, vide, erreur et actions.",
    quandUtiliser: ["Liste métier", "Données serveur"],
    agitSur: "Une page ou zone de données complète.",
    exemples: ["Utilisateurs", "Déclarations", "Composants"],
    import: 'import { TableauDonnees } from "@alpha/patterns";'
  },
  {
    nom: "Pagination",
    famille: "Données",
    statut: "Disponible",
    sertA: "Naviguer entre plusieurs pages de résultats.",
    quandUtiliser: ["Liste longue", "Recherche paginée"],
    agitSur: "Le découpage d'une collection.",
    exemples: ["Page 1 sur 10", "Suivant / Précédent"],
    import: 'import { Pagination } from "@alpha/ui";'
  },
  {
    nom: "PanneauDetails",
    famille: "Données",
    statut: "Pattern",
    sertA: "Afficher le détail d'un élément sélectionné.",
    quandUtiliser: ["Liste + détail", "Inspection rapide"],
    agitSur: "Le contexte de consultation.",
    exemples: ["Détail utilisateur", "Détail déclaration"],
    import: 'import { PanneauDetails } from "@alpha/patterns";'
  },
  {
    nom: "Onglets",
    famille: "Navigation",
    statut: "Disponible",
    sertA: "Séparer plusieurs vues d'un même sujet.",
    quandUtiliser: ["Détails / Historique", "Aperçu / Code"],
    agitSur: "La navigation locale.",
    exemples: ["Profil / Droits / Activité", "Design / Code"],
    import: 'import { Onglets } from "@alpha/ui";'
  },
  {
    nom: "FilAriane",
    famille: "Navigation",
    statut: "Disponible",
    sertA: "Montrer la position dans une hiérarchie.",
    quandUtiliser: ["Organisation > Application > Utilisateur"],
    agitSur: "Le repérage dans l'application.",
    exemples: ["Accueil / Paramètres / Groupes"],
    import: 'import { FilAriane } from "@alpha/ui";'
  },
  {
    nom: "NavigationLaterale",
    famille: "Navigation",
    statut: "Pattern",
    sertA: "Organiser les grandes rubriques d'une application.",
    quandUtiliser: ["Back-office", "Studio", "Portail admin"],
    agitSur: "La structure générale de l'application.",
    exemples: ["Menu Studio", "Menu AlphaTools"],
    import: 'import { NavigationLaterale } from "@alpha/patterns";'
  },
  {
    nom: "BarreLateraleModerne",
    famille: "Navigation",
    statut: "Disponible",
    sertA:
      "Fournir une navigation latérale complète avec recherche, badges, utilisateur, réduction et mode mobile.",
    quandUtiliser: ["Dashboard métier", "Portail admin", "Application avec nombreuses rubriques"],
    agitSur: "La navigation principale de l'interface, pas les routes serveur ni les droits.",
    exemples: [
      "Menu de tableau de bord avec compteurs",
      "Navigation administrateur avec profil connecté",
      "Sidebar mobile avec bouton d'ouverture"
    ],
    import: 'import { BarreLateraleModerne } from "@alpha/ui";'
  },
  {
    nom: "BoiteDialogue",
    famille: "Overlay",
    statut: "Disponible",
    sertA: "Demander une décision bloquante.",
    quandUtiliser: ["Confirmation", "Formulaire court modal"],
    agitSur: "Le flux utilisateur courant.",
    exemples: ["Confirmer suppression", "Modifier un nom"],
    import: 'import { BoiteDialogue } from "@alpha/ui";'
  },
  {
    nom: "Tiroir",
    famille: "Overlay",
    statut: "Disponible",
    sertA: "Afficher un panneau latéral temporaire.",
    quandUtiliser: ["Détail rapide", "Filtres avancés"],
    agitSur: "Une tâche secondaire sans quitter la page.",
    exemples: ["Filtres", "Historique"],
    import: 'import { Tiroir } from "@alpha/ui";'
  },
  {
    nom: "InfoBulle",
    famille: "Overlay",
    statut: "Disponible",
    sertA: "Expliquer une icône ou un terme court.",
    quandUtiliser: ["Bouton icône", "Valeur technique"],
    agitSur: "La compréhension ponctuelle.",
    exemples: ["Rafraîchir", "Commit Git"],
    import: 'import { InfoBulle } from "@alpha/ui";'
  },
  {
    nom: "PageListe",
    famille: "Pages",
    statut: "Pattern",
    sertA: "Construire une page de liste standard.",
    quandUtiliser: ["Utilisateurs", "Applications", "Déclarations"],
    agitSur: "La structure complète d'une page métier.",
    exemples: ["Titre + filtres + actions + tableau"],
    import: 'import { PageListe } from "@alpha/patterns";'
  },
  {
    nom: "PageFormulaire",
    famille: "Pages",
    statut: "Pattern",
    sertA: "Construire une page de création ou modification.",
    quandUtiliser: ["Créer utilisateur", "Modifier organisation"],
    agitSur: "Le parcours de saisie.",
    exemples: ["Titre + formulaire en sections"],
    import: 'import { PageFormulaire } from "@alpha/patterns";'
  },
  {
    nom: "PageErreur",
    famille: "Pages",
    statut: "Pattern",
    sertA: "Afficher une erreur pleine page.",
    quandUtiliser: ["Accès refusé", "Ressource introuvable"],
    agitSur: "La récupération après erreur.",
    exemples: ["403", "Erreur de chargement"],
    import: 'import { PageErreur } from "@alpha/patterns";'
  },
  {
    nom: "Recherche globale",
    famille: "Exemple à créer",
    statut: "Exemple à créer",
    sertA: "Rechercher dans plusieurs collections.",
    quandUtiliser: ["Studio riche", "Back-office multi-entités"],
    agitSur: "L'accès rapide aux contenus.",
    exemples: ["Recherche composant + réglage + brouillon"]
  },
  {
    nom: "Assistant de parcours",
    famille: "Exemple à créer",
    statut: "Exemple à créer",
    sertA: "Guider l'utilisateur selon son objectif.",
    quandUtiliser: ["Produit complexe", "Utilisateur non expert"],
    agitSur: "La compréhension et la réduction des erreurs.",
    exemples: ["Je veux changer une couleur", "Je veux ajouter un composant"]
  },
  {
    nom: "Comparateur de thèmes",
    famille: "Exemple à créer",
    statut: "Exemple à créer",
    sertA: "Comparer clair, sombre et contraste côte à côte.",
    quandUtiliser: ["Avant application des réglages"],
    agitSur: "La validation visuelle.",
    exemples: ["Bouton clair vs sombre", "Tableau contraste"]
  },
  {
    nom: "Journal de changements",
    famille: "Exemple à créer",
    statut: "Exemple à créer",
    sertA: "Expliquer ce qui a changé et pourquoi.",
    quandUtiliser: ["Validation avant livraison"],
    agitSur: "La traçabilité.",
    exemples: ["Réglage changé", "Profil enregistré", "Brouillon supprimé"]
  }
];

function Studio() {
  const [etatSession, definirEtatSession] = useState<EtatSession>("verification");
  const [session, definirSession] = useState<SessionStudio | null>(null);
  const [vue, definirVue] = useState<VueStudio>("charte");
  const [donnees, definirDonnees] = useState<EtatDonnees>(donneesInitiales);
  const [messageGlobal, definirMessageGlobal] = useState("");
  const contenuPrincipal = useRef<HTMLElement | null>(null);
  const vueActive = definitionVue(vue);

  const chargerDonnees = useCallback(async () => {
    definirDonnees((etat) => ({ ...etat, chargement: true, erreur: "" }));
    try {
      const [catalogue, tokens, brouillons, profils, git] = await Promise.all([
        requeteApi<{ composants: ComposantCatalogue[] }>("/api/catalogue"),
        requeteApi<ReponseTokens>("/api/tokens"),
        requeteApi<{ brouillons: BrouillonStudio[] }>("/api/brouillons"),
        requeteApi<{ profils: ProfilApparence[] }>("/api/profils-apparence"),
        requeteApi<StatutGit>("/api/git/statut")
      ]);
      definirDonnees({
        chargement: false,
        catalogue: catalogue.composants,
        tokens,
        brouillons: brouillons.brouillons,
        profils: profils.profils,
        git,
        erreur: ""
      });
    } catch (erreur) {
      definirDonnees((etat) => ({
        ...etat,
        chargement: false,
        erreur: erreur instanceof Error ? erreur.message : "Chargement impossible."
      }));
    }
  }, []);

  useEffect(() => {
    async function verifierSession() {
      if (!lireJetonStudio()) {
        definirEtatSession("connexion");
        return;
      }

      try {
        const reponse = await requeteApi<{ session: SessionStudio }>("/api/session");
        definirSession(reponse.session);
        definirEtatSession("pret");
        await chargerDonnees();
      } catch {
        supprimerJetonStudio();
        definirEtatSession("connexion");
      }
    }

    void verifierSession();
  }, [chargerDonnees]);

  function deconnecter() {
    supprimerJetonStudio();
    definirSession(null);
    definirEtatSession("connexion");
    definirDonnees(donneesInitiales);
  }

  function changerVue(prochaineVue: VueStudio) {
    definirVue(prochaineVue);
    window.requestAnimationFrame(() => {
      contenuPrincipal.current?.scrollIntoView({ block: "start" });
    });
  }

  function rendreBoutonNavigation(element: DefinitionVue) {
    return (
      <button
        aria-current={vue === element.id ? "page" : undefined}
        key={element.id}
        onClick={() => changerVue(element.id)}
        type="button"
      >
        {element.icone}
        <span className="studio-menu-texte">
          <strong>{element.libelle}</strong>
          <small>{element.accrocheNavigation}</small>
        </span>
      </button>
    );
  }

  if (etatSession === "verification") {
    return <ChargementInitial />;
  }

  if (etatSession === "connexion" || !session) {
    return (
      <Connexion
        surConnexion={async (reponse) => {
          if (reponse.jetonAcces && reponse.session) {
            enregistrerJetonStudio(reponse.jetonAcces);
            definirSession(reponse.session);
            definirEtatSession("pret");
            await chargerDonnees();
            return;
          }

          if (reponse.statut?.startsWith("mfa_")) {
            throw new Error(reponse.message || "Validation MFA requise dans AlphaTools.");
          }

          throw new Error("Connexion AlphaTools incomplète.");
        }}
      />
    );
  }

  return (
    <div className="studio-shell">
      <aside className="studio-navigation" aria-label="Navigation Studio">
        <div className="studio-marque">
          <ShieldCheck size={26} />
          <div>
            <strong>Alpha Design Studio</strong>
            <span>Charte graphique</span>
          </div>
        </div>
        <nav className="studio-menu">
          <div className="studio-menu-principal">
            {["charte", "composant", "applications"].map((id) =>
              rendreBoutonNavigation(definitionVue(id as VueStudio)),
            )}
          </div>
        </nav>
      </aside>

      <main className="studio-contenu" ref={contenuPrincipal}>
        <header className="studio-entete">
          <div>
            <h1>{vueActive.libelle}</h1>
            <p>{vueActive.resume}</p>
          </div>
          <GroupeActions>
            <Badge tonalite="neutre">
              {session.utilisateur.nomAffiche || session.utilisateur.nomConnexion}
            </Badge>
            <Badge tonalite={session.modeAcces === "admin_suite" ? "succes" : "information"}>
              {session.modeAcces === "admin_suite" ? "Suite" : "Application"}
            </Badge>
            <BoutonIcone
              etiquette="Rafraîchir"
              icone={<RefreshCw size={18} />}
              onClick={() => void chargerDonnees()}
              title="Rafraîchir"
              variante="secondaire"
            />
            <BoutonIcone
              etiquette="Déconnexion"
              icone={<LogOut size={18} />}
              onClick={deconnecter}
              title="Déconnexion"
              variante="fantome"
            />
          </GroupeActions>
        </header>

        {donnees.erreur ? <Alerte tonalite="danger">{donnees.erreur}</Alerte> : null}
        {messageGlobal ? <Alerte tonalite="information">{messageGlobal}</Alerte> : null}
        {vue === "charte" || vue === "composant" || vue === "applications" ? null : (
          <GuideVue definition={vueActive} />
        )}

        {vue === "guide" ? (
          <VueComprendre surNavigation={changerVue} />
        ) : vue === "controle" ? (
          <VueControle session={session} donnees={donnees} />
        ) : vue === "atelier" ? (
          <VueAtelier
            profils={donnees.profils}
            surChangement={async (message) => {
              definirMessageGlobal(message);
              await chargerDonnees();
            }}
          />
        ) : vue === "charte" ? (
          <Suspense
            fallback={
              <Panneau>
                <IndicateurChargement etiquette="Chargement de la charte graphique" />
              </Panneau>
            }
          >
            <VueCharteGraphique catalogue={donnees.catalogue} />
          </Suspense>
        ) : vue === "tokens" ? (
          <VueTokens
            tokens={donnees.tokens}
            surApplication={async (message) => {
              definirMessageGlobal(message);
              await chargerDonnees();
            }}
          />
        ) : vue === "bibliotheque" ? (
          <VueBibliotheque />
        ) : vue === "catalogue" ? (
          <VueCatalogue composants={donnees.catalogue} surEditionComposant={() => changerVue("composant")} />
        ) : vue === "composant" ? (
          <Suspense
            fallback={
              <Panneau>
                <IndicateurChargement etiquette="Chargement de l'éditeur de composant" />
              </Panneau>
            }
          >
            <VueEditeurComposant
              catalogue={donnees.catalogue}
              surChangement={async (message) => {
                definirMessageGlobal(message);
                await chargerDonnees();
              }}
              surOuvrirBrouillons={() => changerVue("import")}
            />
          </Suspense>
        ) : vue === "applications" ? (
          <Suspense
            fallback={
              <Panneau>
                <IndicateurChargement etiquette="Chargement des applications" />
              </Panneau>
            }
          >
            <VueApplicationsAlpha estAdministrateur={session.modeAcces === "admin_suite"} />
          </Suspense>
        ) : vue === "import" ? (
          <VueImport
            brouillons={donnees.brouillons}
            surOuvrirGraphique={() => changerVue("graphique")}
            surOuvrirComposants={() => changerVue("composant")}
            surChangement={async (message) => {
              definirMessageGlobal(message);
              await chargerDonnees();
            }}
          />
        ) : vue === "graphique" ? (
          <Suspense
            fallback={
              <Panneau>
                <IndicateurChargement etiquette="Chargement de l'éditeur graphique" />
              </Panneau>
            }
          >
            <VueEditeurGraphique
              brouillons={donnees.brouillons}
              catalogue={donnees.catalogue}
              surChangement={async (message) => {
                definirMessageGlobal(message);
                await chargerDonnees();
              }}
              surNavigation={changerVue}
            />
          </Suspense>
        ) : (
          <VueValidation
            git={donnees.git}
            surValidation={(message) => definirMessageGlobal(message)}
          />
        )}
      </main>
    </div>
  );
}

function VueComprendre({ surNavigation }: { surNavigation: (vue: VueStudio) => void }) {
  return (
    <div className="studio-grille">
      <Panneau className="studio-section-large studio-zone-demarrage">
        <div className="studio-section-entete">
          <div>
            <h2>Démarrer par un objectif</h2>
            <p className="studio-texte-aide">
              Choisissez ce que vous voulez faire. Le Studio vous indique le chemin et l'écran à
              ouvrir.
            </p>
          </div>
          <Badge tonalite="information">{parcoursStudio.length} parcours</Badge>
        </div>
        <div className="studio-exemples-grille">
          {parcoursStudio.map((parcours) => (
            <article className="studio-exemple-carte" key={parcours.titre}>
              <h3>{parcours.titre}</h3>
              <p>{parcours.objectif}</p>
              <ol>
                {parcours.etapes.map((etape) => (
                  <li key={etape}>{etape}</li>
                ))}
              </ol>
              <p>
                <strong>Résultat :</strong> {parcours.resultat}
              </p>
              <Bouton onClick={() => surNavigation(parcours.depart)} variante="secondaire">
                <Compass size={16} />
                Ouvrir l'écran utile
              </Bouton>
            </article>
          ))}
        </div>
      </Panneau>

      <Panneau className="studio-section-large">
        <div className="studio-section-entete">
          <div>
            <h2>Lexique opérationnel</h2>
            <p className="studio-texte-aide">
              Chaque notion est expliquée avec définition, utilité, impact, fonctionnement et
              exemples.
            </p>
          </div>
          <Badge tonalite="information">{conceptsStudio.length} notions</Badge>
        </div>
        <div className="studio-concepts-grille">
          {conceptsStudio.map((concept) => (
            <article className="studio-concept-carte" key={concept.nom}>
              <div className="studio-section-entete">
                <h3>{concept.nom}</h3>
                <Bouton
                  onClick={() => surNavigation(concept.action)}
                  taille="compacte"
                  variante="fantome"
                >
                  Ouvrir
                </Bouton>
              </div>
              <dl>
                <div>
                  <dt>Définition</dt>
                  <dd>{concept.definition}</dd>
                </div>
                <div>
                  <dt>À quoi ça sert</dt>
                  <dd>{concept.utilite}</dd>
                </div>
                <div>
                  <dt>Sur quoi ça agit</dt>
                  <dd>{concept.impact}</dd>
                </div>
                <div>
                  <dt>Comment ça fonctionne</dt>
                  <dd>{concept.fonctionnement}</dd>
                </div>
              </dl>
              <div>
                <strong>Exemples concrets</strong>
                <ul>
                  {concept.exemples.map((exemple) => (
                    <li key={exemple}>{exemple}</li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </Panneau>
    </div>
  );
}

function VueBibliotheque() {
  const [requete, definirRequete] = useState("");
  const [famille, definirFamille] = useState("Toutes");
  const familles = useMemo(
    () => ["Toutes", ...new Set(elementsBibliotheque.map((element) => element.famille))],
    []
  );
  const elementsFiltres = elementsBibliotheque.filter((element) => {
    const correspondFamille = famille === "Toutes" || element.famille === famille;
    const texte = `${element.nom} ${element.famille} ${element.statut} ${element.sertA} ${element.quandUtiliser.join(" ")} ${element.exemples.join(" ")}`;
    return correspondFamille && texte.toLowerCase().includes(requete.toLowerCase());
  });

  return (
    <div className="studio-grille">
      <Panneau className="studio-section-large">
        <div className="studio-section-entete">
          <div>
            <h2>Bibliothèque d'exemples</h2>
            <p className="studio-texte-aide">
              Fiches inspirées des pratiques observées dans Material, Carbon, Atlassian et Polaris :
              catégories, cas d'usage, états, impacts et exemples.
            </p>
          </div>
          <Badge tonalite="information">{elementsFiltres.length} éléments</Badge>
        </div>
        <div className="studio-formulaire-grille">
          <GroupeChamp>
            <EtiquetteChamp htmlFor="rechercheBibliotheque">Recherche</EtiquetteChamp>
            <ChampTexte
              id="rechercheBibliotheque"
              onChange={(evenement) => definirRequete(evenement.currentTarget.value)}
              placeholder="bouton, tableau, erreur, formulaire..."
              value={requete}
            />
          </GroupeChamp>
          <GroupeChamp>
            <EtiquetteChamp htmlFor="familleBibliotheque">Famille</EtiquetteChamp>
            <ListeDeroulante
              id="familleBibliotheque"
              onChange={(evenement) => definirFamille(evenement.currentTarget.value)}
              value={famille}
            >
              {familles.map((familleOption) => (
                <option key={familleOption} value={familleOption}>
                  {familleOption}
                </option>
              ))}
            </ListeDeroulante>
          </GroupeChamp>
        </div>
      </Panneau>

      <div className="studio-bibliotheque-grille studio-section-large">
        {elementsFiltres.map((element) => (
          <article className="studio-concept-carte" key={element.nom}>
            <div className="studio-section-entete">
              <h3>{element.nom}</h3>
              <Badge tonalite={tonaliteStatutElement(element.statut)}>{element.statut}</Badge>
            </div>
            <Badge tonalite="neutre">{element.famille}</Badge>
            <p>{element.sertA}</p>
            <dl>
              <div>
                <dt>Quand l'utiliser</dt>
                <dd>{element.quandUtiliser.join(" ; ")}</dd>
              </div>
              <div>
                <dt>Sur quoi ça agit</dt>
                <dd>{element.agitSur}</dd>
              </div>
            </dl>
            <div>
              <strong>Exemples</strong>
              <ul>
                {element.exemples.map((exemple) => (
                  <li key={exemple}>{exemple}</li>
                ))}
              </ul>
            </div>
            {element.import ? <code>{element.import}</code> : null}
          </article>
        ))}
      </div>
    </div>
  );
}

function Connexion({
  surConnexion
}: {
  surConnexion: (reponse: ReponseConnexion) => Promise<void>;
}) {
  const [nomConnexion, definirNomConnexion] = useState("");
  const [motDePasse, definirMotDePasse] = useState("");
  const [codeMfa, definirCodeMfa] = useState("");
  const [mfaTicket, definirMfaTicket] = useState("");
  const [messageMfa, definirMessageMfa] = useState("");
  const [erreur, definirErreur] = useState("");
  const [chargement, definirChargement] = useState(false);
  const estVerificationMfa = Boolean(mfaTicket);

  async function soumettre(evenement: FormEvent<HTMLFormElement>) {
    evenement.preventDefault();
    definirErreur("");
    definirChargement(true);
    try {
      const reponse = estVerificationMfa
        ? await requeteApi<ReponseConnexion>("/api/auth/mfa/verify", {
            method: "POST",
            body: JSON.stringify({ mfaTicket, code: codeMfa })
          })
        : await requeteApi<ReponseConnexion>("/api/auth/login", {
            method: "POST",
            body: JSON.stringify({ nomConnexion, motDePasse })
          });

      if (reponse.statut === "mfa_required" && reponse.mfaTicket) {
        definirMfaTicket(reponse.mfaTicket);
        definirCodeMfa("");
        definirMessageMfa(
          reponse.message || "Saisissez le code de votre application d'authentification."
        );
        return;
      }

      if (reponse.statut === "mfa_enrollment_required") {
        throw new Error(
          reponse.message ||
            "La double authentification doit d'abord être configurée dans AlphaTools."
        );
      }

      await surConnexion(reponse);
    } catch (erreurSoumission) {
      definirErreur(
        erreurSoumission instanceof ErreurApi || erreurSoumission instanceof Error
          ? erreurSoumission.message
          : "Connexion impossible."
      );
    } finally {
      definirChargement(false);
    }
  }

  function revenirAuxIdentifiants() {
    definirMfaTicket("");
    definirCodeMfa("");
    definirMessageMfa("");
    definirErreur("");
  }

  return (
    <main className="studio-connexion">
      <form
        className="studio-formulaire-connexion"
        onSubmit={(evenement) => void soumettre(evenement)}
      >
        <div className="studio-logo-connexion">
          <ShieldCheck size={34} />
        </div>
        <h1>Alpha Design Studio</h1>
        <p className="studio-introduction-connexion">
          Connexion via AlphaTools. Le Studio ne stocke pas votre mot de passe et demandera le code
          MFA si votre compte l'exige.
        </p>
        {!estVerificationMfa ? (
          <>
            <GroupeChamp>
              <EtiquetteChamp htmlFor="nomConnexion">Nom de connexion</EtiquetteChamp>
              <ChampTexte
                autoComplete="username"
                id="nomConnexion"
                onChange={(evenement) => definirNomConnexion(evenement.currentTarget.value)}
                required
                value={nomConnexion}
              />
            </GroupeChamp>
            <GroupeChamp>
              <EtiquetteChamp htmlFor="motDePasse">Mot de passe</EtiquetteChamp>
              <ChampTexte
                autoComplete="current-password"
                id="motDePasse"
                onChange={(evenement) => definirMotDePasse(evenement.currentTarget.value)}
                required
                type="password"
                value={motDePasse}
              />
            </GroupeChamp>
          </>
        ) : (
          <>
            {messageMfa ? <Alerte tonalite="information">{messageMfa}</Alerte> : null}
            <GroupeChamp>
              <EtiquetteChamp htmlFor="codeMfa">Code d'authentification</EtiquetteChamp>
              <ChampTexte
                autoComplete="one-time-code"
                id="codeMfa"
                inputMode="numeric"
                maxLength={16}
                onChange={(evenement) => definirCodeMfa(evenement.currentTarget.value)}
                required
                value={codeMfa}
              />
            </GroupeChamp>
          </>
        )}
        {erreur ? <MessageErreur>{erreur}</MessageErreur> : null}
        <Bouton enChargement={chargement} type="submit">
          {estVerificationMfa ? "Valider le code" : "Connexion AlphaTools"}
        </Bouton>
        {estVerificationMfa ? (
          <Bouton onClick={revenirAuxIdentifiants} type="button" variante="fantome">
            Retour à la connexion
          </Bouton>
        ) : null}
      </form>
    </main>
  );
}

function GuideVue({ definition }: { definition: DefinitionVue }) {
  return (
    <details className="studio-guide" aria-label={`Guide de l'écran ${definition.libelle}`}>
      <summary>
        <span>
          <strong>Guide rapide</strong>
          <small>{definition.actionPrincipale}</small>
        </span>
        <Badge tonalite="information">{definition.exemples.length} exemples</Badge>
      </summary>
      <div className="studio-guide-contenu">
        <div className="studio-guide-bloc">
          <Badge tonalite="information">Où vous êtes</Badge>
          <strong>{definition.libelle}</strong>
          <p>{definition.resume}</p>
        </div>
        <div className="studio-guide-bloc">
          <Badge tonalite="neutre">À quoi ça sert</Badge>
          <p>{definition.objectif}</p>
        </div>
        <div className="studio-guide-bloc studio-guide-etapes">
          <Badge tonalite="succes">Comment s'en servir</Badge>
          <ol>
            {definition.utilisation.map((etape) => (
              <li key={etape}>{etape}</li>
            ))}
          </ol>
        </div>
        <div className="studio-guide-bloc">
          <Badge tonalite="avertissement">Sur quoi ça agit</Badge>
          <p>{definition.impact}</p>
        </div>
        <div className="studio-guide-exemples">
          <div className="studio-section-entete">
            <h2>Exemples concrets</h2>
            <Badge tonalite="information">{definition.exemples.length}</Badge>
          </div>
          <div className="studio-exemples-grille">
            {definition.exemples.map((exemple) => (
              <article className="studio-exemple-carte" key={exemple.titre}>
                <h3>{exemple.titre}</h3>
                <p>
                  <strong>Situation :</strong> {exemple.situation}
                </p>
                <p>
                  <strong>Action :</strong> {exemple.action}
                </p>
                <p>
                  <strong>Résultat :</strong> {exemple.resultat}
                </p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </details>
  );
}

function PanneauExemples({
  titre,
  introduction,
  exemples,
  className = ""
}: {
  titre: string;
  introduction: string;
  exemples: ExempleSimple[];
  className?: string;
}) {
  return (
    <Panneau className={`studio-panneau-exemples ${className}`.trim()}>
      <div className="studio-section-entete">
        <h2>{titre}</h2>
        <Badge tonalite="information">{exemples.length}</Badge>
      </div>
      <p className="studio-texte-aide">{introduction}</p>
      <div className="studio-exemples-grille">
        {exemples.map((exemple) => (
          <article className="studio-exemple-carte" key={exemple.titre}>
            <h3>{exemple.titre}</h3>
            <p>{exemple.description}</p>
            <ul>
              {exemple.details.map((detail) => (
                <li key={detail}>{detail}</li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </Panneau>
  );
}

function VueControle({ session, donnees }: { session: SessionStudio; donnees: EtatDonnees }) {
  const modifications = donnees.git?.statut.trim()
    ? donnees.git.statut.trim().split("\n").length
    : 0;

  return (
    <div className="studio-grille">
      <CarteIndicateurStudio
        icone={<ShieldCheck size={22} />}
        libelle="Accès AlphaTools"
        valeur={
          session.modeAcces === "admin_suite" ? "Administrateur suite" : "Application autorisée"
        }
        tonalite="succes"
      />
      <CarteIndicateurStudio
        icone={<Palette size={22} />}
        libelle="Réglages avancés"
        valeur={String(donnees.tokens?.editeurs.length ?? 0)}
        tonalite="information"
      />
      <CarteIndicateurStudio
        icone={<Layers3 size={22} />}
        libelle="Composants"
        valeur={String(donnees.catalogue.length)}
        tonalite="neutre"
      />
      <CarteIndicateurStudio
        icone={<GitBranch size={22} />}
        libelle="Dépôt"
        valeur={modifications ? `${modifications} changement(s)` : "Propre"}
        tonalite={modifications ? "avertissement" : "succes"}
      />

      <PanneauExemples
        className="studio-section-large"
        exemples={exemplesLectureControle}
        introduction="Ces exemples montrent comment lire les indicateurs avant de toucher au design system."
        titre="Exemples de lecture"
      />

      <Panneau className="studio-section-large">
        <div className="studio-section-entete">
          <h2>Contexte actif</h2>
          <Badge tonalite="information">{donnees.git?.branche || "branche inconnue"}</Badge>
        </div>
        <p className="studio-texte-aide">
          Utilisez ce bloc pour confirmer le compte connecté, le niveau AlphaTools, les applications
          visées et le commit actuellement servi par le Studio.
        </p>
        <TableauDonnees
          colonnes={[
            { id: "cle", titre: "Clé", rendu: (ligne) => ligne.cle, largeur: "14rem" },
            { id: "valeur", titre: "Valeur", rendu: (ligne) => ligne.valeur }
          ]}
          elements={[
            {
              cle: "Utilisateur",
              valeur: session.utilisateur.nomConnexion || session.utilisateur.idUtilisateur
            },
            { cle: "Niveau", valeur: session.utilisateur.niveauAdmin },
            { cle: "Applications", valeur: session.codesApplication.join(", ") },
            {
              cle: "Organisations",
              valeur:
                session.modeAcces === "admin_suite"
                  ? "Toutes, via admin suite"
                  : session.organisations
                      .map((organisation) => organisation.codeOrganisation)
                      .join(", ")
            },
            { cle: "Commit", valeur: donnees.git?.commit || "non lu" }
          ]}
          obtenirCle={(ligne) => ligne.cle}
        />
      </Panneau>

      <Panneau className="studio-section-large">
        <div className="studio-section-entete">
          <h2>État Git</h2>
          <Badge tonalite={modifications ? "avertissement" : "succes"}>
            {modifications ? "Modifié" : "Stable"}
          </Badge>
        </div>
        <p className="studio-texte-aide">
          Cet état sert de garde-fou avant d'appliquer des réglages ou de lancer une validation. Un
          dépôt déjà modifié demande plus de prudence.
        </p>
        <pre className="studio-console">{donnees.git?.statut || "Aucun changement détecté."}</pre>
      </Panneau>
    </div>
  );
}

function VueAtelier({
  profils,
  surChangement
}: {
  profils: ProfilApparence[];
  surChangement: (message: string) => Promise<void>;
}) {
  const [profil, definirProfil] = useState<ProfilApparence>(profilApparenceInitial);
  const [selection, definirSelection] = useState("");
  const [rapport, definirRapport] = useState("");
  const [chargement, definirChargement] = useState(false);

  useEffect(() => {
    if (!selection && profils[0]) {
      definirSelection(profils[0].id);
      definirProfil(profils[0]);
    }
  }, [profils, selection]);

  function mettreAJour<Champ extends keyof ProfilApparence>(
    champ: Champ,
    valeur: ProfilApparence[Champ]
  ) {
    definirProfil((courant) => ({ ...courant, [champ]: valeur }));
  }

  async function enregistrer() {
    definirChargement(true);
    definirRapport("");
    try {
      const reponse = await requeteApi<{ profil: ProfilApparence }>("/api/profils-apparence", {
        method: "POST",
        body: JSON.stringify(profil)
      });
      definirProfil(reponse.profil);
      definirSelection(reponse.profil.id);
      await surChangement("Profil d'apparence enregistré.");
    } catch (erreur) {
      definirRapport(erreur instanceof Error ? erreur.message : "Enregistrement impossible.");
    } finally {
      definirChargement(false);
    }
  }

  async function supprimer() {
    if (!profil.id || profil.id === profilApparenceInitial.id) return;
    if (!window.confirm(`Supprimer le profil ${profil.nom} ?`)) return;
    await requeteApi(`/api/profils-apparence/${encodeURIComponent(profil.id)}`, {
      method: "DELETE"
    });
    definirProfil(profilApparenceInitial);
    definirSelection("");
    await surChangement("Profil d'apparence supprimé.");
  }

  const styleProfil = useMemo(() => creerStyleProfil(profil), [profil]);

  return (
    <div className="studio-layout-atelier">
      <Panneau className="studio-panneau-liste">
        <div className="studio-section-entete">
          <h2>Profils</h2>
          <Badge tonalite="information">{profils.length}</Badge>
        </div>
        <p className="studio-texte-aide">
          Sélectionnez un profil enregistré pour le reprendre, ou démarrez un nouveau profil local.
          Cette liste ne publie rien dans la bibliothèque.
        </p>
        <div className="studio-liste-tokens">
          <button
            aria-current={!selection ? "true" : undefined}
            onClick={() => {
              definirSelection("");
              definirProfil(profilApparenceInitial);
            }}
            type="button"
          >
            <span>Nouveau profil</span>
            <Badge tonalite="neutre">Local</Badge>
          </button>
          {profils.map((profilEnregistre) => (
            <button
              aria-current={selection === profilEnregistre.id ? "true" : undefined}
              key={profilEnregistre.id}
              onClick={() => {
                definirSelection(profilEnregistre.id);
                definirProfil(profilEnregistre);
              }}
              type="button"
            >
              <span>{profilEnregistre.nom}</span>
              <Badge tonalite="succes">Sauvé</Badge>
            </button>
          ))}
        </div>
        <div className="studio-exemples-mini">
          <div className="studio-section-entete">
            <h3>Exemples de profils</h3>
            <Badge tonalite="neutre">{exemplesProfils.length}</Badge>
          </div>
          <p className="studio-texte-aide">
            Chargez un exemple pour comprendre les valeurs typiques avant de créer votre propre
            profil.
          </p>
          {exemplesProfils.map((exemple) => (
            <article className="studio-exemple-carte" key={exemple.titre}>
              <h4>{exemple.titre}</h4>
              <p>{exemple.description}</p>
              <ul>
                {exemple.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
              <Bouton
                onClick={() => {
                  definirSelection("");
                  definirProfil({ ...exemple.profil, id: "", modifieLe: "" });
                }}
                type="button"
                variante="secondaire"
              >
                Utiliser cet exemple
              </Bouton>
            </article>
          ))}
        </div>
      </Panneau>

      <Panneau>
        <div className="studio-section-entete">
          <h2>Réglages visuels</h2>
          <Badge tonalite="information">{profil.styleCarte}</Badge>
        </div>
        <p className="studio-texte-aide">
          Modifiez les champs de haut en bas : identité du profil, typographie, couleurs, puis
          dimensions. L'aperçu à droite se met à jour immédiatement avant enregistrement.
        </p>
        <div className="studio-aide-valeurs">
          <Badge tonalite="neutre">Exemples de valeurs</Badge>
          <ul>
            <li>Police métier : Inter ou Arial.</li>
            <li>Titre dense : 24 px ; titre éditorial : 36 à 40 px.</li>
            <li>Arrondi discret : 6 px ; interface plus douce : 12 à 16 px.</li>
            <li>Image : URL HTTPS publique ou data:image déjà validé.</li>
          </ul>
        </div>
        <div className="studio-formulaire-grille">
          <GroupeChamp>
            <EtiquetteChamp htmlFor="nomProfil">Nom</EtiquetteChamp>
            <ChampTexte
              id="nomProfil"
              onChange={(evenement) => mettreAJour("nom", evenement.currentTarget.value)}
              value={profil.nom}
            />
          </GroupeChamp>
          <GroupeChamp>
            <EtiquetteChamp htmlFor="policeProfil">Police</EtiquetteChamp>
            <ListeDeroulante
              id="policeProfil"
              onChange={(evenement) => mettreAJour("famillePolice", evenement.currentTarget.value)}
              value={profil.famillePolice}
            >
              <option value="Inter, ui-sans-serif, system-ui, sans-serif">Inter</option>
              <option value="Arial, Helvetica, sans-serif">Arial</option>
              <option value="Georgia, serif">Georgia</option>
              <option value="'Courier New', monospace">Courier New</option>
            </ListeDeroulante>
          </GroupeChamp>
          <GroupeChamp>
            <EtiquetteChamp htmlFor="styleProfil">Style</EtiquetteChamp>
            <ListeDeroulante
              id="styleProfil"
              onChange={(evenement) =>
                mettreAJour(
                  "styleCarte",
                  evenement.currentTarget.value as ProfilApparence["styleCarte"]
                )
              }
              value={profil.styleCarte}
            >
              <option value="dense">Dense</option>
              <option value="equilibre">Équilibré</option>
              <option value="editorial">Éditorial</option>
            </ListeDeroulante>
          </GroupeChamp>
        </div>

        <GroupeChamp>
          <EtiquetteChamp htmlFor="descriptionProfil">Description</EtiquetteChamp>
          <ZoneTexte
            id="descriptionProfil"
            onChange={(evenement) => mettreAJour("description", evenement.currentTarget.value)}
            rows={3}
            value={profil.description}
          />
        </GroupeChamp>

        <div className="studio-reglages-couleurs">
          <ChampCouleur
            id="couleurPrincipale"
            libelle="Action"
            surChangement={(valeur) => mettreAJour("couleurPrincipale", valeur)}
            valeur={profil.couleurPrincipale}
          />
          <ChampCouleur
            id="couleurSurface"
            libelle="Surface"
            surChangement={(valeur) => mettreAJour("couleurSurface", valeur)}
            valeur={profil.couleurSurface}
          />
          <ChampCouleur
            id="couleurTexte"
            libelle="Texte"
            surChangement={(valeur) => mettreAJour("couleurTexte", valeur)}
            valeur={profil.couleurTexte}
          />
          <ChampCouleur
            id="couleurAccent"
            libelle="Accent"
            surChangement={(valeur) => mettreAJour("couleurAccent", valeur)}
            valeur={profil.couleurAccent}
          />
          <ChampCouleur
            id="gradientDepart"
            libelle="Dégradé départ"
            surChangement={(valeur) => mettreAJour("gradientDepart", valeur)}
            valeur={profil.gradientDepart}
          />
          <ChampCouleur
            id="gradientArrivee"
            libelle="Dégradé arrivée"
            surChangement={(valeur) => mettreAJour("gradientArrivee", valeur)}
            valeur={profil.gradientArrivee}
          />
        </div>

        <div className="studio-reglages-numeriques">
          <ChampCurseur
            id="tailleBase"
            libelle="Taille texte"
            maximum={22}
            minimum={14}
            surChangement={(valeur) => mettreAJour("tailleBase", valeur)}
            unite="px"
            valeur={profil.tailleBase}
          />
          <ChampCurseur
            id="tailleTitre"
            libelle="Taille titre"
            maximum={44}
            minimum={20}
            surChangement={(valeur) => mettreAJour("tailleTitre", valeur)}
            unite="px"
            valeur={profil.tailleTitre}
          />
          <ChampCurseur
            id="graisseTitre"
            libelle="Graisse titre"
            maximum={800}
            minimum={500}
            pas={100}
            surChangement={(valeur) => mettreAJour("graisseTitre", valeur)}
            valeur={profil.graisseTitre}
          />
          <ChampCurseur
            id="angleDegres"
            libelle="Angle dégradé"
            maximum={360}
            minimum={0}
            surChangement={(valeur) => mettreAJour("angleDegres", valeur)}
            unite="deg"
            valeur={profil.angleDegres}
          />
          <ChampCurseur
            id="arrondi"
            libelle="Arrondi"
            maximum={24}
            minimum={0}
            surChangement={(valeur) => mettreAJour("arrondi", valeur)}
            unite="px"
            valeur={profil.arrondi}
          />
          <ChampCurseur
            id="intensiteOmbre"
            libelle="Ombre"
            maximum={32}
            minimum={0}
            surChangement={(valeur) => mettreAJour("intensiteOmbre", valeur)}
            unite="px"
            valeur={profil.intensiteOmbre}
          />
        </div>

        <GroupeChamp>
          <EtiquetteChamp htmlFor="imageProfil">Image HTTPS ou data:image</EtiquetteChamp>
          <ChampTexte
            id="imageProfil"
            onChange={(evenement) => mettreAJour("imageUrl", evenement.currentTarget.value)}
            placeholder="https://..."
            value={profil.imageUrl}
          />
        </GroupeChamp>

        <GroupeActions>
          <Bouton
            onClick={() => {
              definirSelection("");
              definirProfil({
                ...profil,
                id: "",
                nom: `${profil.nom} copie`
              });
            }}
            variante="secondaire"
          >
            <Copy size={16} />
            Dupliquer
          </Bouton>
          <Bouton enChargement={chargement} onClick={() => void enregistrer()}>
            <Save size={16} />
            Enregistrer
          </Bouton>
          <Bouton disabled={!selection} onClick={() => void supprimer()} variante="danger">
            <Trash2 size={16} />
            Supprimer
          </Bouton>
        </GroupeActions>
        <p className="studio-action-aide">
          Dupliquer crée une copie non enregistrée. Enregistrer écrit dans les profils Studio.
          Supprimer retire uniquement le profil sélectionné.
        </p>
        {rapport ? <MessageErreur>{rapport}</MessageErreur> : null}
      </Panneau>

      <Panneau className="studio-apercu-atelier" style={styleProfil}>
        <div className="studio-section-entete">
          <h2>Aperçu direct</h2>
          <Badge tonalite="succes">Live</Badge>
        </div>
        <p className="studio-texte-aide">
          Cet aperçu montre l'effet visuel du profil en cours. Il sert à décider, pas à publier.
        </p>
        <div className="studio-profil-vitrine" data-style={profil.styleCarte}>
          <div className="studio-profil-hero">
            <div>
              <Badge tonalite="information">Design system</Badge>
              <h3>{profil.nom || "Profil apparence"}</h3>
              <p>{profil.description || "Prévisualisation des couleurs, polices et styles."}</p>
            </div>
            <div className="studio-profil-image" style={creerStyleImage(profil.imageUrl)}>
              <ImageIcon size={38} />
            </div>
          </div>
          <div className="studio-profil-cartes">
            <div>
              <span>Surface</span>
              <strong>Composant</strong>
              <p>Contraste, arrondi, ombre et densité.</p>
            </div>
            <div>
              <span>Action</span>
              <strong>Bouton</strong>
              <button type="button">Valider</button>
            </div>
          </div>
        </div>
      </Panneau>
    </div>
  );
}

function VueTokens({
  tokens,
  surApplication
}: {
  tokens: ReponseTokens | null;
  surApplication: (message: string) => Promise<void>;
}) {
  const [requete, definirRequete] = useState("");
  const [theme, definirTheme] = useState("clair");
  const [selection, definirSelection] = useState<string>("");
  const [valeur, definirValeur] = useState("");
  const [modifications, definirModifications] = useState<SurchargeToken[]>([]);
  const [rapport, definirRapport] = useState<string>("");
  const [chargement, definirChargement] = useState(false);
  const editeurs = useMemo(() => tokens?.editeurs ?? [], [tokens]);
  const cheminsTokensDisponibles = useMemo(
    () => new Set(editeurs.map((editeur) => editeur.chemin)),
    [editeurs]
  );
  const editeursFiltres = editeurs.filter((editeur) =>
    `${editeur.chemin} ${editeur.categorie} ${editeur.nom}`
      .toLowerCase()
      .includes(requete.toLowerCase())
  );
  const editeurActif = editeurs.find((editeur) => editeur.chemin === selection) ?? editeurs[0];

  useEffect(() => {
    if (!selection && editeurs[0]) definirSelection(editeurs[0].chemin);
  }, [editeurs, selection]);

  useEffect(() => {
    if (!editeurActif) return;
    definirValeur(lireValeurToken(editeurActif, theme));
  }, [editeurActif, theme]);

  const styleApercu = useMemo(() => creerStyleApercu(modifications), [modifications]);

  function ajouterModification() {
    if (!editeurActif || !valeur.trim()) return;
    definirModifications((courantes) => [
      ...courantes.filter(
        (modification) =>
          modification.chemin !== editeurActif.chemin || modification.theme !== theme
      ),
      { chemin: editeurActif.chemin, theme, valeur: valeur.trim() }
    ]);
    definirRapport("Modification ajoutée à la simulation.");
  }

  function preparerExempleToken(exemple: ExempleToken) {
    if (!cheminsTokensDisponibles.has(exemple.chemin)) return;
    definirSelection(exemple.chemin);
    definirTheme(exemple.theme);
    definirValeur(exemple.valeur);
    definirRapport(`Exemple chargé : ${exemple.chemin}.${exemple.theme} = ${exemple.valeur}`);
  }

  function retirerModification(chemin: string, themeModification?: string) {
    definirModifications((courantes) =>
      courantes.filter(
        (modification) => modification.chemin !== chemin || modification.theme !== themeModification
      )
    );
    definirRapport("Modification retirée de la simulation.");
  }

  async function envoyer(appliquer: boolean) {
    definirChargement(true);
    definirRapport("");
    try {
      const reponse = await requeteApi<{
        erreurs: string[];
        avertissements: string[];
        statut?: string;
      }>(appliquer ? "/api/tokens/appliquer" : "/api/tokens/simulation", {
        method: "POST",
        body: JSON.stringify({ surcharges: modifications })
      });
      const details = [...reponse.erreurs, ...reponse.avertissements].join("\n");
      definirRapport(details || (appliquer ? "Réglages appliqués." : "Simulation valide."));
      if (appliquer && !reponse.erreurs.length)
        await surApplication("Réglages appliqués et fichiers générés.");
    } catch (erreur) {
      definirRapport(erreur instanceof Error ? erreur.message : "Action impossible.");
    } finally {
      definirChargement(false);
    }
  }

  return (
    <div className="studio-layout-edition">
      <Panneau className="studio-panneau-liste">
        <div className="studio-section-entete">
          <h2>Réglages avancés</h2>
          <Badge tonalite="information">{editeurs.length}</Badge>
        </div>
        <p className="studio-texte-aide">
          Cette liste contient les valeurs modifiables utilisées par les interfaces. Sélectionnez un
          réglage pour voir sa valeur actuelle et préparer un changement.
        </p>
        <ChampTexte
          aria-label="Rechercher un réglage"
          onChange={(evenement) => definirRequete(evenement.currentTarget.value)}
          placeholder="Rechercher"
          value={requete}
        />
        <div className="studio-liste-tokens">
          {editeursFiltres.map((editeur) => (
            <button
              aria-current={editeur.chemin === editeurActif?.chemin ? "true" : undefined}
              key={editeur.chemin}
              onClick={() => definirSelection(editeur.chemin)}
              type="button"
            >
              <span>{editeur.chemin}</span>
              {editeur.surchargeApplicative ? <Badge tonalite="succes">Modifié</Badge> : null}
            </button>
          ))}
        </div>
      </Panneau>

      <Panneau>
        <div className="studio-section-entete">
          <h2>Modification</h2>
          <Badge tonalite="neutre">{theme}</Badge>
        </div>
        <p className="studio-texte-aide">
          Travaillez en deux temps : ajoutez les valeurs à la simulation, puis simulez ou appliquez
          la liste complète. Rien n'est écrit tant que vous ne cliquez pas sur Appliquer.
        </p>
        <div className="studio-aide-valeurs">
          <Badge tonalite="neutre">Exemples de formats</Badge>
          <ul>
            <li>Couleur : #1f6feb ou #0f7b4b.</li>
            <li>Police : Inter, ui-sans-serif, system-ui, sans-serif.</li>
            <li>Densité : 0.9 pour compact, 1 pour standard, 1.1 pour plus aéré.</li>
          </ul>
        </div>
        <div className="studio-formulaire-grille">
          <GroupeChamp>
            <EtiquetteChamp htmlFor="themeToken">Thème</EtiquetteChamp>
            <ListeDeroulante
              id="themeToken"
              onChange={(evenement) => definirTheme(evenement.currentTarget.value)}
              value={theme}
            >
              <option value="clair">Clair</option>
              <option value="sombre">Sombre</option>
              <option value="contraste">Contraste</option>
            </ListeDeroulante>
          </GroupeChamp>
          <GroupeChamp>
            <EtiquetteChamp htmlFor="valeurToken">Valeur</EtiquetteChamp>
            <ChampTexte
              id="valeurToken"
              onChange={(evenement) => definirValeur(evenement.currentTarget.value)}
              value={valeur}
            />
          </GroupeChamp>
          {editeurActif?.chemin.startsWith("couleurs.") ? (
            <GroupeChamp>
              <EtiquetteChamp htmlFor="couleurToken">Couleur</EtiquetteChamp>
              <ChampTexte
                id="couleurToken"
                onChange={(evenement) => definirValeur(evenement.currentTarget.value)}
                type="color"
                value={valeur.match(/^#[0-9a-f]{6}$/iu) ? valeur : "#0067b8"}
              />
            </GroupeChamp>
          ) : null}
        </div>
        <div className="studio-exemples-mini">
          <div className="studio-section-entete">
            <h3>Exemples de réglages</h3>
            <Badge tonalite="neutre">{exemplesTokens.length}</Badge>
          </div>
          <p className="studio-texte-aide">
            Chargez un exemple pour voir quel champ il prépare. Le bouton est désactivé si le
            réglage n'est pas disponible dans le catalogue chargé.
          </p>
          {exemplesTokens.map((exemple) => {
            const estDisponible = cheminsTokensDisponibles.has(exemple.chemin);
            return (
              <article className="studio-exemple-carte" key={exemple.titre}>
                <h4>{exemple.titre}</h4>
                <p>{exemple.description}</p>
                <code>
                  {exemple.chemin}.{exemple.theme} = {exemple.valeur}
                </code>
                <ul>
                  {exemple.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
                <Bouton
                  disabled={!estDisponible}
                  onClick={() => preparerExempleToken(exemple)}
                  type="button"
                  variante="secondaire"
                >
                  {estDisponible ? "Charger cet exemple" : "Réglage absent"}
                </Bouton>
              </article>
            );
          })}
        </div>
        <GroupeActions>
          <Bouton onClick={ajouterModification} variante="secondaire">
            <SlidersHorizontal size={16} />
            Ajouter
          </Bouton>
          <Bouton
            disabled={!modifications.length}
            enChargement={chargement}
            onClick={() => void envoyer(false)}
            variante="secondaire"
          >
            <Eye size={16} />
            Simuler
          </Bouton>
          <Bouton
            disabled={!modifications.length}
            enChargement={chargement}
            onClick={() => void envoyer(true)}
          >
            <Save size={16} />
            Appliquer
          </Bouton>
        </GroupeActions>
        <p className="studio-action-aide">
          Ajouter prépare une modification locale. Simuler vérifie la demande. Appliquer modifie les
          fichiers techniques de la charte et lance la génération.
        </p>

        <div className="studio-modifications">
          {modifications.length ? (
            modifications.map((modification) => (
              <div
                className="studio-modification"
                key={`${modification.chemin}-${modification.theme}`}
              >
                <code>
                  {modification.chemin}.{modification.theme} = {modification.valeur}
                </code>
                <BoutonIcone
                  etiquette={`Retirer ${modification.chemin}`}
                  icone={<Trash2 size={16} />}
                  onClick={() => retirerModification(modification.chemin, modification.theme)}
                  variante="fantome"
                />
              </div>
            ))
          ) : (
            <p className="studio-texte-aide">Aucune modification en attente.</p>
          )}
        </div>
        {rapport ? <pre className="studio-console">{rapport}</pre> : null}
      </Panneau>

      <Panneau className="studio-apercu" style={styleApercu}>
        <div className="studio-section-entete">
          <h2>Aperçu direct</h2>
          <Badge tonalite="succes">Live</Badge>
        </div>
        <p className="studio-texte-aide">
          L'aperçu applique seulement les modifications ajoutées à la simulation de cet écran.
        </p>
        <div className="studio-vitrine">
          <div>
            <h3>Interface Alpha</h3>
            <p>Police, taille, couleur principale, surface et contraste.</p>
            <GroupeActions>
              <Bouton>Action principale</Bouton>
              <Bouton variante="secondaire">Secondaire</Bouton>
            </GroupeActions>
          </div>
          <div className="studio-vitrine-image" aria-label="Aperçu image et dégradé" />
        </div>
      </Panneau>
    </div>
  );
}

function VueCatalogue({
  composants,
  surEditionComposant
}: {
  composants: ComposantCatalogue[];
  surEditionComposant: () => void;
}) {
  const [requete, definirRequete] = useState("");
  const resultats = composants.filter((composant) =>
    `${composant.nom} ${composant.package} ${composant.description}`
      .toLowerCase()
      .includes(requete.toLowerCase())
  );

  return (
    <div className="studio-grille">
      <PanneauExemples
        className="studio-section-large"
        exemples={exemplesCatalogue}
        introduction="Ces exemples montrent quoi rechercher selon le composant ou le pattern dont vous avez besoin."
        titre="Exemples de recherche"
      />
      <Panneau className="studio-section-large">
        <div className="studio-section-entete">
          <div>
            <h2>Catalogue composants</h2>
            <p className="studio-texte-aide">
              Recherchez ici avant de créer ou importer un élément. Le tableau indique le composant,
              le package à utiliser et l'import exact à copier.
            </p>
          </div>
          <GroupeActions>
            <Badge tonalite="information">{resultats.length}</Badge>
            <Bouton onClick={surEditionComposant} type="button" variante="secondaire">
              <Settings size={16} />
              Éditer graphiquement
            </Bouton>
          </GroupeActions>
        </div>
        <ChampTexte
          aria-label="Rechercher dans le catalogue"
          onChange={(evenement) => definirRequete(evenement.currentTarget.value)}
          placeholder="Rechercher"
          value={requete}
        />
        <TableauDonnees
          colonnes={[
            {
              id: "nom",
              titre: "Composant",
              rendu: (composant) => <strong>{composant.nom}</strong>
            },
            { id: "package", titre: "Package", rendu: (composant) => composant.package },
            {
              id: "description",
              titre: "Description",
              rendu: (composant) => composant.description
            },
            {
              id: "import",
              titre: "Import",
              rendu: (composant) => <code>{composant.import}</code>
            }
          ]}
          elements={resultats}
          obtenirCle={(composant) => `${composant.package}-${composant.nom}`}
        />
      </Panneau>
    </div>
  );
}

function detecterBarreLateraleImport(contenu: string): boolean {
  return /modern-side-bar|navigationItems|SidebarProps|export\s+function\s+Sidebar|const\s+Sidebar|w-78|sidebar/iu.test(
    contenu
  );
}

function compatibilitesImport(contenu: string, mode: ModeImportStudio) {
  return [
    {
      libelle: "Mode d'entrée",
      actif: true,
      detail:
        mode === "url"
          ? "Le Studio lit l'URL seulement si l'option serveur est activée."
          : "Le Studio analyse le contenu saisi dans la zone Source."
    },
    {
      libelle: "React / TypeScript",
      actif: /React|tsx|jsx|interface\s+[A-Z]|export\s+function|export\s+const/u.test(contenu),
      detail: "Un composant React ou une structure TSX est détecté."
    },
    {
      libelle: "shadcn ou Next",
      actif: /"use client"|components\/ui|shadcn/u.test(contenu),
      detail: "Le code vise une structure Next/shadcn ; il doit être adapté à @alpha/ui."
    },
    {
      libelle: "Tailwind CSS",
      actif: /className=["'`][^"'`]*(?:bg-|text-|flex|grid|rounded|shadow|md:|h-|w-)/u.test(
        contenu
      ),
      detail: "Des classes Tailwind sont présentes ; le Studio les remplace par des styles Alpha."
    },
    {
      libelle: "lucide-react",
      actif: /lucide-react/u.test(contenu),
      detail: "Les icônes lucide-react sont compatibles avec la bibliothèque Alpha."
    }
  ];
}

function DiagnosticImportPanel({
  contenu,
  mode,
  rapport
}: {
  contenu: string;
  mode: ModeImportStudio;
  rapport: RapportImport | null;
}) {
  const compatibilites = compatibilitesImport(contenu, mode);
  const barreLateraleDetectee = detecterBarreLateraleImport(contenu);

  return (
    <Panneau className="studio-diagnostic-import">
      <div className="studio-section-entete">
        <h2>Ce qui se passe avec cet import</h2>
        <Badge tonalite={barreLateraleDetectee ? "succes" : "information"}>
          {barreLateraleDetectee ? "Aperçu disponible" : "Diagnostic"}
        </Badge>
      </div>
      <p className="studio-texte-aide">
        Le code collé n'est pas exécuté directement dans le navigateur. Le Studio l'analyse, le
        conserve en brouillon si vous l'enregistrez, puis un humain ou un patch le transforme en
        composant Alpha quand il doit devenir réutilisable.
      </p>

      <div className="studio-diagnostic-grille">
        <article className="studio-diagnostic-carte">
          <strong>1. Source</strong>
          <p>
            Vous collez du code, du texte ou une URL. Ici, la zone Source est la donnée analysée.
          </p>
        </article>
        <article className="studio-diagnostic-carte">
          <strong>2. Analyse</strong>
          <p>
            Le bouton Analyser détecte exports, dépendances, couleurs en dur et conventions
            externes.
          </p>
        </article>
        <article className="studio-diagnostic-carte">
          <strong>3. Brouillon</strong>
          <p>Enregistrer écrit dans .alpha/studio-brouillons, sans publier dans @alpha/ui.</p>
        </article>
        <article className="studio-diagnostic-carte">
          <strong>4. Adaptation</strong>
          <p>Quand le besoin est valide, le composant est réécrit avec les réglages Alpha.</p>
        </article>
      </div>

      <div className="studio-compatibilites">
        {compatibilites.map((compatibilite) => (
          <article className="studio-compatibilite" key={compatibilite.libelle}>
            <Badge tonalite={compatibilite.actif ? "succes" : "neutre"}>
              {compatibilite.actif ? "Détecté" : "Non détecté"}
            </Badge>
            <strong>{compatibilite.libelle}</strong>
            <p>{compatibilite.detail}</p>
          </article>
        ))}
      </div>

      {barreLateraleDetectee ? (
        <>
          <Alerte tonalite="information">
            <CheckCircle2 size={16} />
            Import reconnu : ce code correspond à une barre latérale moderne. Le Studio affiche
            ci-dessous l'adaptation Alpha réellement intégrée : <code>BarreLateraleModerne</code>.
          </Alerte>
          <ApercuImportBarreLaterale />
        </>
      ) : (
        <Alerte tonalite="avertissement">
          <AlertTriangle size={16} />
          Aucun aperçu spécialisé n'est disponible pour cette source. Le brouillon reste utile pour
          préparer le composant, mais il ne deviendra visible dans la bibliothèque qu'après
          adaptation.
        </Alerte>
      )}

      {rapport ? (
        <p className="studio-action-aide">
          Dernière analyse : {rapport.composantsDetectes.length || 0} composant(s) détecté(s),
          statut {rapport.statut}.
        </p>
      ) : (
        <p className="studio-action-aide">
          Lancez Analyser pour obtenir le rapport serveur. L'aperçu adapté se met à jour dès que la
          source ressemble à une sidebar.
        </p>
      )}
    </Panneau>
  );
}

function ApercuImportBarreLaterale() {
  const [selection, definirSelection] = useState("dashboard");
  const libelles: Record<string, string> = {
    dashboard: "Dashboard",
    analytics: "Analytics",
    documents: "Documents",
    notifications: "Notifications",
    profile: "Profil",
    settings: "Paramètres",
    help: "Aide"
  };

  return (
    <div className="studio-apercu-import-sidebar">
      <div className="studio-section-entete">
        <h3>Aperçu adapté Alpha</h3>
        <Badge tonalite="succes">BarreLateraleModerne</Badge>
      </div>
      <div className="studio-apercu-import-corps">
        <BarreLateraleModerne
          elements={[
            { id: "dashboard", libelle: "Dashboard", description: "Vue de départ", icone: Home },
            { id: "analytics", libelle: "Analytics", description: "Mesures", icone: BarChart3 },
            { id: "documents", libelle: "Documents", badge: "3", icone: FileText },
            { id: "notifications", libelle: "Notifications", badge: "12", icone: Bell },
            { id: "profile", libelle: "Profil", icone: User },
            { id: "settings", libelle: "Paramètres", icone: Settings },
            { id: "help", libelle: "Aide", icone: HelpCircle }
          ]}
          nomProduit="Acme Corp"
          selection={selection}
          sousTitreProduit="Enterprise Dashboard"
          surSelection={definirSelection}
          utilisateur={{ nom: "John Doe", role: "Senior Administrator", statut: "en-ligne" }}
        />
        <div className="studio-apercu-import-explication">
          <strong>Section sélectionnée dans l'aperçu : {libelles[selection]}</strong>
          <p>
            Cette prévisualisation agit seulement sur l'état local de l'aperçu. Elle ne change pas
            les routes, les droits AlphaTools ni les brouillons.
          </p>
          <code>import {"{ BarreLateraleModerne }"} from "@alpha/ui";</code>
        </div>
      </div>
    </div>
  );
}

function VueImport({
  brouillons,
  surOuvrirGraphique,
  surOuvrirComposants,
  surChangement
}: {
  brouillons: BrouillonStudio[];
  surOuvrirGraphique: () => void;
  surOuvrirComposants: () => void;
  surChangement: (message: string) => Promise<void>;
}) {
  const [mode, definirMode] = useState<ModeImportStudio>("code");
  const [nom, definirNom] = useState("BarreLateraleModerne");
  const [url, definirUrl] = useState("");
  const [contenu, definirContenu] = useState(exempleBarreLaterale);
  const [rapport, definirRapport] = useState<RapportImport | null>(null);
  const [promotion, definirPromotion] = useState<PromotionBrouillon | null>(null);
  const [erreur, definirErreur] = useState("");
  const [chargement, definirChargement] = useState(false);
  const [publicationEnCours, definirPublicationEnCours] = useState("");

  async function analyser() {
    definirChargement(true);
    definirErreur("");
    try {
      const reponse = await requeteApi<RapportImport>("/api/import/analyser", {
        method: "POST",
        body: JSON.stringify({ mode, nom, url, contenu })
      });
      definirRapport(reponse);
    } catch (erreurAnalyse) {
      definirRapport(null);
      definirErreur(erreurAnalyse instanceof Error ? erreurAnalyse.message : "Analyse impossible.");
    } finally {
      definirChargement(false);
    }
  }

  async function enregistrer() {
    definirChargement(true);
    definirErreur("");
    try {
      await requeteApi<{ brouillon: BrouillonStudio }>("/api/brouillons", {
        method: "POST",
        body: JSON.stringify({
          nom,
          type: "composant",
          source: mode === "url" ? url : mode,
          contenu
        })
      });
      await surChangement(
        mode === "code"
          ? "Brouillon enregistré. Retrouvez-le dans Composants."
          : "Brouillon enregistré."
      );
      if (mode === "code") surOuvrirComposants();
    } catch (erreurEnregistrement) {
      definirErreur(
        erreurEnregistrement instanceof Error
          ? erreurEnregistrement.message
          : "Enregistrement impossible."
      );
    } finally {
      definirChargement(false);
    }
  }

  async function supprimer(id: string) {
    if (!window.confirm("Supprimer ce brouillon Studio ?")) return;
    definirErreur("");
    try {
      await requeteApi(`/api/brouillons/${encodeURIComponent(id)}`, { method: "DELETE" });
      await surChangement("Brouillon supprimé.");
    } catch (erreurSuppression) {
      definirErreur(
        erreurSuppression instanceof Error ? erreurSuppression.message : "Suppression impossible."
      );
    }
  }

  async function promouvoir(brouillon: BrouillonStudio) {
    definirPublicationEnCours(brouillon.id);
    definirErreur("");
    definirPromotion(null);
    try {
      const reponse = await requeteApi<{ promotion: PromotionBrouillon }>(
        `/api/brouillons/${encodeURIComponent(brouillon.id)}/promouvoir`,
        {
          method: "POST",
          body: JSON.stringify({ cible: "ui" })
        }
      );
      definirPromotion(reponse.promotion);
      await surChangement(
        `Composant ${reponse.promotion.nom} créé dans @alpha/ui. Import : ${reponse.promotion.import}`
      );
    } catch (erreurPromotion) {
      definirErreur(
        erreurPromotion instanceof Error ? erreurPromotion.message : "Création du composant impossible."
      );
    } finally {
      definirPublicationEnCours("");
    }
  }

  function chargerExempleImport(exemple: ExempleImportStudio) {
    definirMode(exemple.mode);
    definirNom(exemple.nom);
    definirUrl(exemple.url);
    definirContenu(exemple.contenu);
    definirRapport(null);
    definirErreur("");
  }

  return (
    <div className="studio-layout-edition">
      <Panneau>
        <div className="studio-section-entete">
          <h2>Ajout ou import</h2>
          <Badge tonalite="information">{mode}</Badge>
        </div>
        <p className="studio-texte-aide">
          Déposez une proposition ici pour l'analyser avant de la conserver. L'analyse lit la source
          et signale les erreurs ; l'enregistrement crée seulement un brouillon Studio.
        </p>
        <div className="studio-formulaire-grille">
          <GroupeChamp>
            <EtiquetteChamp htmlFor="modeImport">Mode</EtiquetteChamp>
            <ListeDeroulante
              id="modeImport"
              onChange={(evenement) =>
                definirMode(evenement.currentTarget.value as ModeImportStudio)
              }
              value={mode}
            >
              <option value="code">Code</option>
              <option value="texte">Texte</option>
              <option value="url">URL</option>
            </ListeDeroulante>
          </GroupeChamp>
          <GroupeChamp>
            <EtiquetteChamp htmlFor="nomBrouillon">Nom</EtiquetteChamp>
            <ChampTexte
              id="nomBrouillon"
              onChange={(evenement) => definirNom(evenement.currentTarget.value)}
              value={nom}
            />
          </GroupeChamp>
          {mode === "url" ? (
            <GroupeChamp>
              <EtiquetteChamp htmlFor="urlImport">URL</EtiquetteChamp>
              <ChampTexte
                id="urlImport"
                onChange={(evenement) => definirUrl(evenement.currentTarget.value)}
                placeholder="https://"
                value={url}
              />
            </GroupeChamp>
          ) : null}
        </div>
        <div className="studio-exemples-mini">
          <div className="studio-section-entete">
            <h3>Exemples de sources</h3>
            <Badge tonalite="neutre">{exemplesImports.length}</Badge>
          </div>
          <p className="studio-texte-aide">
            Utilisez un exemple pour comprendre le rôle de chaque mode avant de coller votre propre
            contenu.
          </p>
          {exemplesImports.map((exemple) => (
            <article className="studio-exemple-carte" key={exemple.titre}>
              <h4>{exemple.titre}</h4>
              <p>{exemple.description}</p>
              <Badge tonalite="information">{exemple.mode}</Badge>
              <ul>
                {exemple.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
              <Bouton
                onClick={() => chargerExempleImport(exemple)}
                type="button"
                variante="secondaire"
              >
                Utiliser cet exemple
              </Bouton>
            </article>
          ))}
        </div>
        <GroupeChamp>
          <EtiquetteChamp htmlFor="contenuImport">Source</EtiquetteChamp>
          <ZoneTexte
            id="contenuImport"
            onChange={(evenement) => definirContenu(evenement.currentTarget.value)}
            rows={14}
            value={contenu}
          />
        </GroupeChamp>
        {mode === "url" ? (
          <Alerte tonalite="avertissement">
            L'import URL peut être désactivé côté serveur. Les hôtes locaux ou privés restent
            refusés par sécurité.
          </Alerte>
        ) : null}
        <GroupeActions>
          <Bouton enChargement={chargement} onClick={() => void analyser()} variante="secondaire">
            <Braces size={16} />
            Analyser
          </Bouton>
          <Bouton enChargement={chargement} onClick={() => void enregistrer()}>
            <Save size={16} />
            Enregistrer
          </Bouton>
        </GroupeActions>
        <p className="studio-action-aide">
          Analyser ne sauvegarde rien. Enregistrer écrit dans les brouillons. Pour publier,
          utilisez ensuite Créer composant @alpha/ui sur le brouillon concerné.
        </p>
        {erreur ? <MessageErreur>{erreur}</MessageErreur> : null}
        {rapport ? <RapportImportPanel rapport={rapport} /> : null}
      </Panneau>

      <div className="studio-colonne-droite">
        <DiagnosticImportPanel contenu={contenu} mode={mode} rapport={rapport} />

        <Panneau>
          <div className="studio-section-entete">
            <h2>Brouillons</h2>
            <Badge tonalite="information">{brouillons.length}</Badge>
          </div>
          <p className="studio-texte-aide">
            Ouvrir remplace le contenu de l'éditeur par le brouillon choisi. Supprimer retire
            seulement le brouillon Studio. Créer composant @alpha/ui ajoute un export réel dans le
            design system sans effacer le brouillon.
          </p>
          {promotion ? (
            <Alerte
              tonalite={promotion.generationCatalogue.code === 0 ? "information" : "avertissement"}
            >
              Composant créé : <strong>{promotion.nom}</strong>. Import à utiliser :{" "}
              <code>{promotion.import}</code>.{" "}
              {promotion.generationCatalogue.code === 0
                ? "Catalogue régénéré."
                : "Catalogue à régénérer dans l'écran Valider."}
            </Alerte>
          ) : null}
          <div className="studio-brouillons">
            {brouillons.map((brouillon) => (
              <Carte key={brouillon.id} className="studio-brouillon">
                <div>
                  <strong>{brouillon.nom}</strong>
                  <span>{new Date(brouillon.modifieLe).toLocaleString("fr-FR")}</span>
                </div>
                <GroupeActions>
                  <Bouton
                    onClick={() => {
                      definirNom(brouillon.nom);
                      definirContenu(brouillon.contenu);
                      if (brouillon.source === "code" || brouillon.source === "texte") {
                        definirMode(brouillon.source);
                        definirUrl("");
                      } else {
                        definirMode("url");
                        definirUrl(brouillon.source);
                      }
                    }}
                    variante="secondaire"
                  >
                    <FileCode2 size={16} />
                    Ouvrir
                  </Bouton>
                  {brouillon.source === "code" ? (
                    <>
                      <Bouton onClick={surOuvrirGraphique} type="button" variante="secondaire">
                        <MousePointer2 size={16} />
                        Ouvrir graphique
                      </Bouton>
                    </>
                  ) : null}
                  {brouillon.source === "code" ||
                  brouillon.source === "editeur-live-react" ||
                  brouillon.source === "editeur-composant-graphique-v1" ? (
                    <Bouton
                      enChargement={publicationEnCours === brouillon.id}
                      onClick={() => void promouvoir(brouillon)}
                      type="button"
                      variante="secondaire"
                    >
                      <Boxes size={16} />
                      Créer composant @alpha/ui
                    </Bouton>
                  ) : null}
                  <BoutonIcone
                    etiquette={`Supprimer ${brouillon.nom}`}
                    icone={<Trash2 size={16} />}
                    onClick={() => void supprimer(brouillon.id)}
                    variante="danger"
                  />
                </GroupeActions>
              </Carte>
            ))}
          </div>
        </Panneau>
      </div>
    </div>
  );
}

function VueValidation({
  git,
  surValidation
}: {
  git: StatutGit | null;
  surValidation: (message: string) => void;
}) {
  const [commande, definirCommande] = useState("tests");
  const [sortie, definirSortie] = useState("");
  const [chargement, definirChargement] = useState(false);

  async function lancer() {
    definirChargement(true);
    definirSortie("");
    try {
      const reponse = await requeteApi<{
        commande: string;
        code: number;
        sortie: string;
        erreur: string;
      }>("/api/validation/lancer", {
        method: "POST",
        body: JSON.stringify({ commande })
      });
      definirSortie(`${reponse.commande}\n\n${reponse.sortie}\n${reponse.erreur}`.trim());
      surValidation(reponse.code === 0 ? "Validation terminée." : "Validation en échec.");
    } catch (erreur) {
      definirSortie(erreur instanceof Error ? erreur.message : "Validation impossible.");
    } finally {
      definirChargement(false);
    }
  }

  return (
    <div className="studio-layout-edition">
      <Panneau>
        <div className="studio-section-entete">
          <h2>Commandes contrôlées</h2>
          <Badge tonalite="information">{git?.branche || "git"}</Badge>
        </div>
        <p className="studio-texte-aide">
          Ces commandes tournent sur le serveur Studio avec votre session AlphaTools. Elles servent
          à contrôler le dépôt avant livraison.
        </p>
        <GroupeChamp>
          <EtiquetteChamp htmlFor="commandeValidation">Commande</EtiquetteChamp>
          <ListeDeroulante
            id="commandeValidation"
            onChange={(evenement) => definirCommande(evenement.currentTarget.value)}
            value={commande}
          >
            <option value="catalogue">Catalogue</option>
            <option value="tests">Tests</option>
            <option value="types">Types</option>
            <option value="build">Build</option>
            <option value="verifier">Vérification complète</option>
          </ListeDeroulante>
        </GroupeChamp>
        <div className="studio-commande-detail">
          <Badge tonalite="neutre">Effet de la commande</Badge>
          <p>{descriptionCommandeValidation(commande)}</p>
        </div>
        <div className="studio-exemples-mini">
          <div className="studio-section-entete">
            <h3>Exemples de commandes</h3>
            <Badge tonalite="neutre">{exemplesValidation.length}</Badge>
          </div>
          {exemplesValidation.map((exemple) => (
            <article className="studio-exemple-carte" key={exemple.titre}>
              <h4>{exemple.titre}</h4>
              <p>{exemple.description}</p>
              <ul>
                {exemple.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
        <Interrupteur checked readOnly>
          Exécution serveur authentifiée
        </Interrupteur>
        <GroupeActions>
          <Bouton enChargement={chargement} onClick={() => void lancer()}>
            <Play size={16} />
            Lancer
          </Bouton>
        </GroupeActions>
        <p className="studio-action-aide">
          Lancer exécute seulement la commande sélectionnée. La vérification complète peut durer
          plus longtemps que les autres contrôles.
        </p>
      </Panneau>
      <Panneau>
        <div className="studio-section-entete">
          <h2>Sortie</h2>
          <Badge tonalite={sortie ? "information" : "neutre"}>
            {sortie ? "Disponible" : "Vide"}
          </Badge>
        </div>
        <p className="studio-texte-aide">
          La sortie affiche la commande, les logs et les erreurs. Un échec doit être corrigé avant
          de considérer le changement livrable.
        </p>
        <pre className="studio-console">{sortie || "Aucune commande lancée."}</pre>
      </Panneau>
    </div>
  );
}

function CarteIndicateurStudio({
  icone,
  libelle,
  valeur,
  tonalite
}: {
  icone: ReactElement;
  libelle: string;
  valeur: string;
  tonalite: "neutre" | "succes" | "danger" | "information" | "avertissement";
}) {
  return (
    <Carte className="studio-indicateur">
      <span>{icone}</span>
      <div>
        <Badge tonalite={tonalite}>{libelle}</Badge>
        <strong>{valeur}</strong>
      </div>
    </Carte>
  );
}

function ChampCouleur({
  id,
  libelle,
  valeur,
  surChangement
}: {
  id: string;
  libelle: string;
  valeur: string;
  surChangement: (valeur: string) => void;
}) {
  return (
    <GroupeChamp className="studio-champ-couleur">
      <EtiquetteChamp htmlFor={id}>{libelle}</EtiquetteChamp>
      <div>
        <ChampTexte
          aria-label={`${libelle} valeur`}
          onChange={(evenement) => surChangement(evenement.currentTarget.value)}
          type="color"
          value={valeur.match(/^#[0-9a-f]{6}$/iu) ? valeur : "#0067b8"}
        />
        <ChampTexte
          id={id}
          onChange={(evenement) => surChangement(evenement.currentTarget.value)}
          value={valeur}
        />
      </div>
    </GroupeChamp>
  );
}

function ChampCurseur({
  id,
  libelle,
  maximum,
  minimum,
  pas = 1,
  unite = "",
  valeur,
  surChangement
}: {
  id: string;
  libelle: string;
  maximum: number;
  minimum: number;
  pas?: number;
  unite?: string;
  valeur: number;
  surChangement: (valeur: number) => void;
}) {
  return (
    <GroupeChamp className="studio-champ-curseur">
      <EtiquetteChamp htmlFor={id}>{libelle}</EtiquetteChamp>
      <input
        id={id}
        max={maximum}
        min={minimum}
        onChange={(evenement) => surChangement(Number(evenement.currentTarget.value))}
        step={pas}
        type="range"
        value={valeur}
      />
      <span>
        {valeur}
        {unite}
      </span>
    </GroupeChamp>
  );
}

function RapportImportPanel({ rapport }: { rapport: RapportImport }) {
  return (
    <div className="studio-rapport-import">
      <Badge tonalite={rapport.statut === "pret" ? "succes" : "avertissement"}>
        {rapport.statut}
      </Badge>
      {rapport.erreurs.map((erreur) => (
        <Alerte key={erreur} tonalite="danger">
          <AlertTriangle size={16} />
          {erreur}
        </Alerte>
      ))}
      {rapport.avertissements.map((avertissement) => (
        <Alerte key={avertissement} tonalite="avertissement">
          <AlertTriangle size={16} />
          {avertissement}
        </Alerte>
      ))}
      <pre className="studio-console">{rapport.apercu}</pre>
    </div>
  );
}

function ChargementInitial() {
  return (
    <main className="studio-chargement">
      <CheckCircle2 size={28} />
      <span>Vérification AlphaTools</span>
    </main>
  );
}

function definitionVue(vue: VueStudio): DefinitionVue {
  const element = vues.find((vueCandidate) => vueCandidate.id === vue);
  const premiereVue = vues[0];
  if (!premiereVue) throw new Error("Aucune vue Studio configurée.");
  return element ?? premiereVue;
}

function descriptionCommandeValidation(commande: string): string {
  const descriptions: Record<string, string> = {
    catalogue: "Régénère le catalogue de composants à partir des sources du design system.",
    tests: "Lance les tests automatisés Vitest disponibles pour vérifier les règles métier.",
    types: "Contrôle TypeScript sur le monorepo pour détecter les erreurs de contrat.",
    build: "Compile les packages et applications concernés pour vérifier la construction.",
    verifier:
      "Lance la recette complète du dépôt : format, lint, types, tests, build et contrôles projet."
  };
  return descriptions[commande] ?? "Commande inconnue.";
}

function tonaliteStatutElement(
  statut: ElementBibliotheque["statut"]
): "neutre" | "succes" | "danger" | "information" | "avertissement" {
  if (statut === "Disponible") return "succes";
  if (statut === "Pattern") return "information";
  return "avertissement";
}

function lireValeurToken(editeur: EditeurToken, theme: string): string {
  if (typeof editeur.valeur === "string") return editeur.valeur;
  return editeur.valeur[theme] ?? Object.values(editeur.valeur)[0] ?? "";
}

function creerStyleApercu(modifications: SurchargeToken[]): CSSProperties {
  const style: CSSProperties & Record<string, string> = {};
  for (const modification of modifications) {
    if (modification.chemin === "couleurs.actionPrincipale") {
      style["--alpha-couleurs-action-principale"] = modification.valeur;
    }
    if (modification.chemin === "couleurs.fondSurfaceAccent") {
      style["--alpha-couleurs-fond-surface-accent"] = modification.valeur;
    }
    if (modification.chemin === "typographie.familleTexte") {
      style["--alpha-typographie-famille-texte"] = modification.valeur;
    }
    if (modification.chemin === "typographie.tailleTexte") {
      style["--alpha-typographie-taille-texte"] = modification.valeur;
    }
    if (modification.chemin === "typographie.tailleTitre") {
      style["--alpha-typographie-taille-titre"] = modification.valeur;
    }
    if (modification.chemin === "rayons.moyen") {
      style["--alpha-rayons-moyen"] = modification.valeur;
    }
  }
  return style;
}

function creerStyleProfil(profil: ProfilApparence): CSSProperties & Record<string, string> {
  return {
    "--studio-profil-police": profil.famillePolice,
    "--studio-profil-taille-base": `${profil.tailleBase}px`,
    "--studio-profil-taille-titre": `${profil.tailleTitre}px`,
    "--studio-profil-graisse-titre": String(profil.graisseTitre),
    "--studio-profil-couleur-principale": profil.couleurPrincipale,
    "--studio-profil-couleur-surface": profil.couleurSurface,
    "--studio-profil-couleur-texte": profil.couleurTexte,
    "--studio-profil-couleur-accent": profil.couleurAccent,
    "--studio-profil-gradient": `${profil.angleDegres}deg, ${profil.gradientDepart}, ${profil.gradientArrivee}`,
    "--studio-profil-arrondi": `${profil.arrondi}px`,
    "--studio-profil-ombre": `0 ${Math.max(2, profil.intensiteOmbre / 2)}px ${
      profil.intensiteOmbre
    }px rgb(21 32 43 / 0.18)`
  };
}

function creerStyleImage(imageUrl: string): CSSProperties | undefined {
  if (!imageUrl.trim()) return undefined;
  if (imageUrl.startsWith("data:image/") || URL.canParse(imageUrl)) {
    return { backgroundImage: `url("${imageUrl.replaceAll('"', "%22")}")` };
  }
  return undefined;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Studio />
  </StrictMode>
);
